import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { cloneDeep, mapValues } from 'lodash';
import { addDays, getDay, isBefore, isValid } from 'date-fns';
import i18next from 'i18next';
import { generateId } from '../utils/generate-id';
import { toDate } from '../utils/circuitDate';
// Types
import { Column } from '../objects/column';
import { CircuitState } from '../objects/circuitState';
import { CircuitLocalized } from '../objects/circuitLocalized';
import { Preferences } from '../../profile/objects/preferences';
import { ListNetworkData } from '../../utils/list/objects/listNetworkData';
import { Margin } from '../../margin/objects/margin';
// Action Types
import {
    ADD_CIRCUIT_PERIOD,
    ADD_PREFERENCES,
    CircuitActionTypes,
    DELETE_CIRCUIT_PERIOD,
    DELETE_CIRCUIT_PICTURE, DELETE_CIRCUIT_PRICE,
    MOVE_DOWN_COLUMN,
    MOVE_UP_COLUMN,
    RESET_ADD_CIRCUIT,
    RESET_FILTERS,
    SET_ACCOMODATION,
    SET_BO_ACCOMODATION,
    SET_CAR,
    SET_CIRCUIT,
    SET_CIRCUIT_PRICES,
    SET_CUSTOM_PRODUCTS,
    SET_EDIT,
    SET_EDIT_STEP,
    SET_EDIT_CMS,
    SET_ERRORS,
    SET_EXPORT_TYPE,
    SET_FLIGHT,
    SET_LIMIT,
    SET_LIST,
    SET_MANUAL_PRODUCTS,
    SET_TRAINS,
    SET_MASTER_PRICE,
    SET_ORDER_BY,
    SET_PAGE,
    SET_PREFERENCES as SET_CIRCUIT_PREFERENCES,
    SET_PRICES_FLIGHT,
    SET_PRICES_TERRESTRIAL,
    SET_PRODUCT_PERIOD,
    SET_RUNNING,
    SET_SEARCH,
    SET_TRANSFERS,
    TOGGLE_CANCEL_MODAL,
    TOGGLE_CIRCUIT_PICTURES_ORDER,
    TOGGLE_COLUMN,
    TOGGLE_CONTRACT_CREATE,
    TOGGLE_CREATE,
    TOGGLE_EXPORT,
    TOGGLE_EXPORT_FULL,
    TOGGLE_FILTERS,
    TOGGLE_REFRESH,
    UPDATE_CATEGORIES,
    UPDATE_CIRCUIT_NAME_LOCALE,
    UPDATE_CIRCUIT_NAME_LOCALES,
    UPDATE_CIRCUIT_PICTURE_ORDER,
    UPDATE_CONTRACT,
    UPDATE_CONTRACT_SELECTED,
    UPDATE_DESTINATION,
    UPDATE_DURATION_DAYS,
    UPDATE_DURATION_NIGHTS,
    UPDATE_NB_OF_ISLANDS_INCLUDED,
    UPDATE_FILTER_CATEGORIES,
    UPDATE_FILTER_CMS,
    UPDATE_FILTER_COUNTRY,
    UPDATE_FILTER_CREATED_DATE_GTE,
    UPDATE_FILTER_CREATED_DATE_LTE,
    UPDATE_FILTER_CREATOR,
    UPDATE_FILTER_DESTINATION,
    UPDATE_FILTER_SOURCE,
    UPDATE_FILTER_PROVIDERS,
    UPDATE_FILTER_LANGUAGES,
    UPDATE_FILTER_LAST_AUTHOR,
    UPDATE_FILTER_REFERENCE,
    UPDATE_FILTER_NB_OF_ISLAND_INCLUDED,
    UPDATE_FILTER_MODIFIED_DATE_GTE,
    UPDATE_FILTER_MODIFIED_DATE_LTE,
    UPDATE_FILTER_PERIOD,
    UPDATE_FILTER_PERIOD_TYPE,
    UPDATE_FILTER_STATUS,
    UPDATE_FILTER_TYPE,
    UPDATE_LIST_ITEM_VISIBILITY,
    UPDATE_LIST_ITEM_PROTECTED_DESCRIPTION,
    UPDATE_PERIOD_DAYS,
    UPDATE_PERIOD_END_DATE,
    UPDATE_PERIOD_START_DATE,
    UPDATE_PRICE_TYPE,
    UPDATE_PRODUCTS,
    UPDATE_PRODUCTS_STACK,
    UPDATE_PRODUCTS_REFERENCE_PRODUCT_TO_REPLACE,
    UPDATE_PROVIDER,
    UPDATE_REFERENCE,
    UPDATE_TRANSPORT_IN_ARRIVAL_DATE,
    UPDATE_EXTERNAL_REFERENCE,
    UPDATE_DISPLAY_FOR_PACKAGE,
    UPDATE_DISPLAY_FOR_TRIP_PLAN,
    UPDATE_RETURN_FLIGHT,
    UPDATE_FLIGHT_PROPOSED,
    UPDATE_FLIGHT_PRICE_INDICATIVE,
    UPDATE_SALE_PITCH_LOCALE,
    UPDATE_TRIP,
    UPDATE_TYPE,
    SET_DUPLICATE_CIRCUIT,
    UPDATE_SELECTED_CIRCUIT,
    UPDATE_MANY_CIRCUIT_VISIBILITY,
    SET_COLUMNS,
    SET_IS_PROTECTED_DESCRIPTION,
    UPDATE_EDIT_STOCK_STATE,
    UPDATE_CIRCUIT_REAL_STOCK,
    UPDATE_CIRCUIT_REAL_STOCKS,
    UPDATE_FILTER_PACKAGE_TYPES,
    SET_ALLOWED_BO_ACCOMODATION,
    UPDATE_FILTER_IS_PROTECTED_DESCRIPTION
} from './actionTypes';
import { ADD_CUSTOM_PICTURES, PictureActionTypes, SET_PICTURE } from '../../picture/redux/actionTypes';
import { ProfileActionTypes, SET_PREFERENCES } from '../../profile/redux/actionTypes';
import { Day, PricePeriod } from '../objects/price-period';
import { TerrestrialPrice } from '../objects/terrestrialPrice';
import { ModelPrice } from '../../priceModel/objects/modelPrice';
import { FlightPrice } from '../objects/flightPrice';
import { Airport } from '../objects/airport';
import { Promotion } from '../objects/promotion';
import { Itinerary } from '../objects/itinerary';
import { ItineraryByDay } from '../objects/itineraryByDay';
import { Product } from '../objects/product';
import { Picture } from '../../picture/objects/picture';
import { Contract } from '../../customProvider/objects/contract';
import {
    PricesParametersDatesMode,
    PricesParametersDatesSelection
} from '../circuitEditPricesFormParameters';
import { Currency } from '../../currency/objects/currency';
import { FlightParams } from '../objects/flightParams';
import { FlightParamsListConfiguration } from '../objects/flightParamsListConfiguration';
import { FlightParamsListCriteria } from '../objects/flightParamsListCriteria';
import { setFlightParamsPreference } from '../utils/setFlightParamsPreference';
import { EMPTY_CIRCUIT_INFO_ERRORS, EMPTY_FLIGHT_PARAMS_LIST_CONFIGURATION, EMPTY_FLIGHT_PARAMS_LIST_CRITERIA } from '../constants/flightParamsConstants';
import { CircuitInfoErrors } from '../objects/circuitInfoErrors';
import { CircuitDatesForm } from '../circuitGirDate';
import { CircuitCalendar } from '../circuitStockStateCalendar';
import { CircuitContractParameters } from '../objects/circuitContractParameters';
import { DepartureAirport } from '../../departureAirport/objects/departureAirport';
import { DepartureAirportsParams } from '../objects/departureAirportsParams';
import { CircuitRoom } from '../objects/circuitRoom';
import { CircuitRoomListConfiguration, CircuitRoomListCriteria } from '../objects/circuitRoomList';
import { EMPTY_ROOM_LIST_CONFIGURATION, EMPTY_ROOM_LIST_CRITERIA } from '../constants/roomConstants';
import { setRoomPreference } from '../utils/setRoomPreference';
import { EMPTY_MEAL_PLAN_LIST_CONFIGURATION, EMPTY_MEAL_PLAN_LIST_CRITERIA } from '../constants/mealPlanConstants';
import { CircuitMealPlan } from '../objects/circuitMealPlan';
import { CircuitMealPlanListConfiguration, CircuitMealPlanListCriteria } from '../objects/circuitMealPlanList';
import { setMealPlanPreference } from '../utils/setMealPlanPreference';
import { Cms } from '../../cms/objects/cms';
import { SpecialOffer, SpecialOfferListConfiguration, SpecialOfferListCriteria } from '../objects/specialOffer';
import { EMPTY_SPECIAL_OFFER_LIST_CONFIGURATION, EMPTY_SPECIAL_OFFER_LIST_CRITERIA } from '../utils/specialOffer';
import { setSpecialOfferPreference } from '../utils/setSpecialOfferPreference';
import { DEFAULT_COLUMN } from '../constants/circuitConstant';
import { CircuitRoomingListExportCriteria } from '../objects/circuitRoomingListExportCriteria';
import { RoomingListGeneration } from '../objects/roomingListGeneration';
import { RoomingListParams } from '../../circuitRoomingListParams/objects/roomingListParams';
import { RoomingListHistory, RoominglistHistoryCriteria } from '../objects/roomingListHistory';
import { EMPTY_CIRCUIT_ROOMING_LIST_EXPORT_CRITERIA, EMPTY_ROOMING_LIST_HISOTRY_CRITERIA } from '../constants/circuitRoomingListConstants';
import { CircuitTripProductToReplace } from '../circuitProductReplacementFormTripList';
import { setDepartureAirportsPreference } from '../utils/setDepartureAirportsPreference';
import { CircuitCreateStepperDataPackageType } from '../circuitCreateStepper';
import { MarginListCriteria } from '../objects/marginListCriteria';
import { CircuitMarginListConfiguration } from '../objects/marginListConfiguration';
import { CIRCUIT_MARGIN_MODULE, EMPTY_CIRCUIT_MARGIN_LIST_CONFIGURATION, EMPTY_CIRCUIT_MARGIN_LIST_CRITERIA } from '../constants/circuitMarginConstants';
import { setCircuitMarginPreference } from '../utils/setCircuitMarginPreference';

const initialState: CircuitState = {
    selected_circuit: [],
    cancel_modal: false,
    columns: DEFAULT_COLUMN,
    contract_create: false,
    contract_contract: null,
    contract_contract_error: '',
    contract_provider: null,
    count: 0,
    create: false,
    edit: null,
    edit_step: 'VERSION_AND_VARIANT',
    edit_cms: 'INFO',
    error: '',
    editor_pictures: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    circuit_name_locales: {},
    filter_country: null,
    filter_categories: null,
    filter_cms: [],
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_destination: null,
    filter_sources: [],
    filter_providers: [],
    filter_languages: [],
    filter_last_author: '',
    filter_reference: '',
    filter_nb_of_islands_included: '',
    filter_modified_date_gte: null,
    forfait_price: false,
    flight_included: true,
    flight_proposed: false,
    flight_price_indicative: false,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period_type: 0,
    filter_period: 'CREATION',
    filter_status: 'ALL',
    filter_type: 'ALL',
    filter_package_types: [CircuitCreateStepperDataPackageType.UNKNOWN],
    filter_is_protected_description: undefined,
    destination: null,
    type: null,
    categories: null,
    sale_pitch_locales: {},
    reference: '',
    transport_in_arrival_date: false,
    external_reference: '',
    is_protected_description: false,
    display_for_package: false,
    display_for_trip_plan: false,
    duration_days: 0,
    duration_nights: 0,
    nb_of_islands_included: '',
    limit: 50,
    list: null,
    name_error: '',
    name: '',
    order_by: 'name',
    order: 'asc',
    page: 0,
    preferences: [],
    refresh: false,
    running: false,
    search: '',
    circuit_pictures: [],
    circuit_pictures_order: false,
    circuit: null,
    providers: [],
    contract_selected: null,
    accomoDation: null,
    boAccomoDation: null,
    allowedBoAccomoDation: null,
    flight: null,
    car: null,
    transfers: null,
    trip: [],
    manualProducts: null,
    trains: null,
    customProducts: null,
    prices: null,
    prices_master: null,
    duplicate_circuit: null,
    edit_stock_state: false
};

const updateCircuitPeriodProperty = (value: any, price_index: number, index: number, state: CircuitState, key: string) => {
    const room_prices = [...(state.prices_master ?? [])];
    const price = { ...room_prices[price_index] };
    const periods = [...price.periods];
    const period = { ...periods[index] };
    period[key] = value;
    periods[index] = period;
    price.periods = periods;
    room_prices[price_index] = price;
    return { ...state, prices_master: room_prices };
};

export default (state: CircuitState = initialState, action: CircuitActionTypes | ProfileActionTypes | PictureActionTypes): CircuitState => {
    switch (action.type) {
        case UPDATE_CIRCUIT_REAL_STOCK: {
            return {
                ...state, circuit: state.circuit ? ({
                    ...state.circuit,
                    real_stocks: state.circuit.real_stocks.find(rs => rs.id === action.payload.id) ?
                        state.circuit.real_stocks.map(rs => rs.id === action.payload.id ? action.payload : rs) :
                        [...state.circuit.real_stocks, action.payload]
                }) : null
            };
        }
        case UPDATE_CIRCUIT_REAL_STOCKS: {
            return {
                ...state, circuit: state.circuit ? ({
                    ...state.circuit,
                    real_stocks: action.payload
                }) : null
            };
        }
        case UPDATE_EDIT_STOCK_STATE: {
            return { ...state, edit_stock_state: action.payload };
        }
        case ADD_PREFERENCES: {
            return { ...state, preferences: [...state.preferences, ...action.payload] };
        }
        case ADD_CUSTOM_PICTURES: {
            return { ...state, circuit_pictures: [...state.circuit_pictures, ...action.payload] };
        }
        case ADD_CIRCUIT_PERIOD: {
            const room_prices = [...(state.prices_master ?? [])];
            const price = { ...room_prices[action.payload] };
            const periods = [...price.periods];
            periods.push({
                id: periods.length + 1,
                start_date: null,
                start_date_error: '',
                end_date: null,
                end_date_error: '',
                start_days: [0, 1, 2, 3, 4, 5, 6],
                start_days_error: '',
            });
            price.periods = periods;
            room_prices[action.payload] = price;
            return { ...state, prices_master: room_prices };
        }
        case DELETE_CIRCUIT_PICTURE: {
            const circuit_pictures = [...state.circuit_pictures];
            circuit_pictures.splice(action.payload, 1);
            return { ...state, circuit_pictures: circuit_pictures };
        }
        case MOVE_DOWN_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload + 1] };
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case MOVE_UP_COLUMN: {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = { ...columns[action.payload - 1] };
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case RESET_ADD_CIRCUIT: {
            return {
                ...state,
                cancel_modal: false,
                edit_step: 'INFO',
                circuit_name_locales: {},
                destination: null,
                type: null,
                reference: '',
                transport_in_arrival_date: false,
                external_reference: '',
                is_protected_description: false,
                display_for_package: false,
                display_for_trip_plan: false,
                categories: null,
                sale_pitch_locales: {},
                duration_days: 0,
                duration_nights: 0,
                nb_of_islands_included: '',
                circuit_pictures: [],
                circuit_pictures_order: false,
                contract_provider: null,
                contract_contract: null,
                contract_contract_error: ''
            };
        }
        case RESET_FILTERS: {
            return {
                ...state,
                filter_categories: null,
    filter_cms: [],
                filter_created_date_gte: null,
                filter_created_date_lte: null,
                filter_creator: '',
                filter_destination: null,
                filter_sources: [],
                filter_providers: [],
                filter_languages: [],
                filter_last_author: '',
                filter_reference: '',
                filter_nb_of_islands_included: '',
                filter_modified_date_gte: null,
                filter_modified_date_lte: null,
                filter_period: 'CREATION',
                filter_period_type: 0,
                filter_status: 'ALL',
                filter_type: 'ALL',
                filter_package_types: [CircuitCreateStepperDataPackageType.UNKNOWN],
                filter_is_protected_description: undefined
            };
        }
        case SET_CIRCUIT: {
            const { current_locale, circuit } = action.payload;
            if (circuit) {
                const circuit_name_locales: Record<any, any> = {};
                const sale_pitch_locales: Record<any, any> = {};
                let has_french = false;
                const providers = [...circuit.contracts];
                providers.map(provider => {
                    provider.contracts = [];
                    const contracts = [...circuit.contracts];
                    for (let i = 0; i < contracts.length; i++) {
                        if (provider.id === contracts[i].provider?.id) {
                            provider.contracts.push({ ...contracts[i] });
                        }
                    }
                });

                circuit.localization.map((locale: CircuitLocalized) => {
                    if (locale.name) {
                        has_french = true;
                    }
                    circuit_name_locales[locale.locale] = locale.name;
                    sale_pitch_locales[locale.locale] = locale.sale_pitch || '';
                });
                if (!has_french) {
                    circuit_name_locales[current_locale] = circuit.name || '';
                }
                const contract_selected = circuit.contracts.find((contract: Contract) => contract.id === state.contract_selected?.id);
                return {
                    ...state,
                    circuit: { ...circuit, guide_languages: circuit.guide_languages ?? [] },
                    duration_days: circuit.duration_day,
                    duration_nights: circuit.duration_night,
                    nb_of_islands_included: circuit.nb_of_islands_included ?? '',
                    reference: circuit.reference,
                    transport_in_arrival_date: circuit.transport_in_arrival_date,
                    external_reference: circuit.external_reference,
                    is_protected_description: circuit.is_protected_description,
                    display_for_package: circuit.display_for_package,
                    display_for_trip_plan: circuit.display_for_trip_plan,
                    categories: circuit.categories,
                    circuit_pictures: circuit.pictures,
                    type: circuit.type,
                    circuit_name_locales: circuit_name_locales,
                    sale_pitch_locales: sale_pitch_locales,
                    forfait_price: circuit.forfait_price,
                    flight_included: circuit.flight_included,
                    trip: circuit.trip?.data ?? [],
                    contract_selected: contract_selected ?? null,
                    providers: providers,
                };
            } else {
                return {
                    ...state,
                    // circuit: {id: state.circuit?.id},
                    circuit_name_locales: {},
                    destination: null,
                    type: null,
                    reference: '',
                    transport_in_arrival_date: false,
                    external_reference: '',
                    is_protected_description: false,
                    display_for_package: false,
                    display_for_trip_plan: false,
                    categories: null,
                    sale_pitch_locales: {},
                    duration_days: 0,
                    duration_nights: 0,
                    nb_of_islands_included: '',
                    circuit_pictures: [],
                    flight_included: false,
                    trip: [],
                    providers: []
                };
            }
        }
        case SET_ACCOMODATION: {
            return { ...state, accomoDation: action.payload };

        }
        case SET_BO_ACCOMODATION: {
            return { ...state, boAccomoDation: action.payload };
        }
        case SET_ALLOWED_BO_ACCOMODATION: {
            return { ...state, allowedBoAccomoDation: action.payload };
        }
        case SET_MANUAL_PRODUCTS: {
            return { ...state, manualProducts: action.payload };
        }
        case SET_TRAINS: {
            return { ...state, trains: action.payload };
        }
        case SET_CIRCUIT_PRICES: {
            if (action.payload === false) {
                return { ...state, prices: null };
            } else {
                return { ...state, prices: action.payload };
            }
        }
        case SET_CUSTOM_PRODUCTS: {
            return { ...state, customProducts: action.payload };
        }
        case SET_FLIGHT: {
            return { ...state, flight: action.payload };
        }
        case SET_CAR: {
            return { ...state, car: action.payload };
        }
        case SET_TRANSFERS: {
            return { ...state, transfers: action.payload };
        }
        case SET_CIRCUIT_PREFERENCES: {
            return { ...state, preferences: action.payload };
        }
        case SET_EDIT_STEP: {
            return { ...state, edit_step: action.payload, edit_cms: 'INFO' };
        }
        case SET_EDIT_CMS: {
            return { ...state, edit_cms: action.payload };
        }
        case SET_EDIT: {
            return { ...state, edit: action.payload };
        }
        case SET_MASTER_PRICE: {
            if (action.payload === false) {
                return { ...state, prices_master: null };
            } else {
                return { ...state, prices_master: action.payload };
            }
        }
        case SET_PRODUCT_PERIOD: {
            return { ...state, product_period: action.payload };
        }
        case SET_PRICES_FLIGHT: {
            return { ...state, prices_flight: action.payload };
        }
        case SET_PRICES_TERRESTRIAL: {
            return { ...state, prices_terrestrial: action.payload };
        }
        case SET_PICTURE: {
            if (action.payload !== true && action.payload !== false) {
                const circuit_pictures = [...state.circuit_pictures];
                for (let i = 0; i < circuit_pictures.length; i++) {
                    if (circuit_pictures[i].id === action.payload.id) {
                        circuit_pictures[i] = { ...action.payload };
                        break;
                    }
                }
                const circuitRootPictures = [...state.circuit?.pictures ?? []];
                for (let i = 0; i < circuitRootPictures.length; i++) {
                    if (circuitRootPictures[i]?.id === action.payload.id) {
                        circuitRootPictures[i] = { ...action.payload };
                        break;
                    }
                }
                return {
                    ...state,
                    circuit: state.circuit ?
                        {
                            ...state.circuit,
                            map_picture: state.circuit.map_picture?.id === action.payload.id ?
                                action.payload :
                                state.circuit.map_picture,
                            pictures: circuitRootPictures
                        } :
                        null,
                    circuit_pictures: circuit_pictures
                };
            }
            return state;
        }
        case SET_EXPORT_TYPE: {
            return { ...state, export_type: action.payload };
        }
        case SET_ERRORS: {
            return { ...state, error: action.payload };
        }
        case TOGGLE_CONTRACT_CREATE: {
            return { ...state, contract_create: !state.contract_create, contract_contract: null, contract_contract_error: '', contract_provider: null };
        }
        case SET_LIMIT: {
            return { ...state, list: null, limit: action.payload };
        }
        case SET_LIST: {
            const { count, results } = action.payload;
            return { ...state, count: count, list: results, running: false };
        }
        case SET_ORDER_BY: {
            return { ...state, list: null, order_by: action.payload, order: state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc' };
        }
        case SET_PAGE: {
            return { ...state, list: null, page: action.payload };
        }
        case SET_PREFERENCES: {
            const preferences = action.payload;
            const new_columns: Column[] = [];
            let new_order_by = '-created_date';
            let new_order: 'asc' | 'desc' = 'asc';
            const field_name: string[] = [];
            const circuit_preferences: Preferences[] = [];
            preferences.map(preference => {
                if (preference.module === 'CIRCUIT') {
                    if (!field_name.includes(preference.field_name)) {
                        switch (preference.field_name) {
                            case 'NAME':
                                new_columns.push({ label: 'name', field_name: preference.field_name, checked: preference.active, order_by: 'name', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'name' : new_order_by;
                                break;
                            case 'SUPPLIER_NAME':
                                new_columns.push({ label: 'supplier-name', field_name: preference.field_name, checked: preference.active, order_by: 'to_name', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'to_name' : new_order_by;
                                break;
                            case 'PROTECTED_DESCRIPTION':
                                new_columns.push({ label: 'protected-description', field_name: preference.field_name, checked: preference.active, order_by: 'is_protected_description', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'is_protected_description' : new_order_by;
                                break;
                            case 'PROVIDERS':
                                new_columns.push({ label: 'providers', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                break;
                            case 'LANGUAGES':
                                new_columns.push({ label: 'languages', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                break;
                            case 'SOURCE':
                                new_columns.push({ label: 'source', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                break;
                            case 'REFERENCE':
                                new_columns.push({ label: 'reference', field_name: preference.field_name, checked: preference.active, order_by: 'reference', fixed_width: false, last_updated: false });
                                break;
                            case 'TYPE':
                                new_columns.push({ label: 'type', field_name: preference.field_name, checked: preference.active, order_by: 'type', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'type' : new_order_by;
                                break;
                            case 'PACKAGE_STRUCTURE_TYPE':
                                new_columns.push({ label: 'package-structure-type', field_name: preference.field_name, checked: preference.active, order_by: 'package_type', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'package_type' : new_order_by;
                                break;
                            case 'DESTINATION':
                                new_columns.push({ label: 'destination', field_name: preference.field_name, checked: preference.active, order_by: 'destination__current_version__localization__name', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'destination__current_version__localization__name' : new_order_by;
                                break;
                            case 'COUNTRY':
                                new_columns.push({ label: 'country', field_name: preference.field_name, checked: preference.active, order_by: 'destination__current_version__country__international_name', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'destination__current_version__country__international_name' : new_order_by;
                                break;
                            case 'CATEGORIES':
                                new_columns.push({ label: 'categories', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? '' : new_order_by;
                                break;
                            case 'VISIBILITY':
                                new_columns.push({ label: 'online-to', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false });
                                break;
                            case 'AGENCY_VISIBILITY':
                                new_columns.push({ label: 'online-b2b', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false });
                                break;
                            case 'ISLAND_NUMBER':
                                new_columns.push({ label: 'island-number', field_name: preference.field_name, checked: preference.active, order_by: 'nb_of_islands_included', fixed_width: true, last_updated: false });
                                break;
                            case 'ALLOWED_CMS':
                                new_columns.push({ label: 'allowed-cms', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                break;
                            case 'CREATED_DATE':
                                new_columns.push({ label: 'created-date', field_name: preference.field_name, checked: preference.active, order_by: 'created_date', fixed_width: true, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'created_date' : new_order_by;
                                break;
                            case 'CREATED_BY':
                                new_columns.push({ label: 'created-by', field_name: preference.field_name, checked: preference.active, order_by: 'creator__username', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'creator__username' : new_order_by;
                                break;
                            case 'MODIFIED_DATE':
                                new_columns.push({ label: 'modified-date', field_name: preference.field_name, checked: preference.active, order_by: 'modified_date', fixed_width: true, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'modified_date' : new_order_by;
                                break;
                            case 'MODIFIED_BY':
                                new_columns.push({ label: 'modified-by', field_name: preference.field_name, checked: preference.active, order_by: 'last_author__username', fixed_width: false, last_updated: false });
                                new_order_by = preference.ordering_asc !== null ? 'last_author__username' : new_order_by;
                                break;
                            default:
                                break;
                        }
                        circuit_preferences.push(preference);
                        field_name.push(preference.field_name);
                        new_order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : new_order;
                    }
                }
            });
            if (new_columns.length !== state.columns.length) {
                state.columns.map(column => {
                    let added = false;
                    for (let i = 0; i < new_columns.length; i++) {
                        if (new_columns[i].label === column.label) {
                            added = true;
                            break;
                        }
                    }
                    if (!added) {
                        new_columns.push({ ...column });
                    }
                });
            }
            return { ...state, preferences: circuit_preferences, columns: new_columns, order_by: new_order_by, order: new_order };
        }
        case SET_RUNNING: {
            return { ...state, running: action.payload };
        }
        case SET_SEARCH: {
            return { ...state, list: null, page: 0, search: action.payload };
        }
        case TOGGLE_COLUMN: {
            const columns = [...state.columns];
            const column = { ...columns[action.payload] };
            column.checked = !column.checked;
            columns[action.payload] = column;
            return { ...state, columns: columns };
        }
        case SET_COLUMNS: {
            return { ...state, columns: action.payload };
        }
        case TOGGLE_CREATE: {
            return { ...state, create: !state.create };
        }
        case TOGGLE_CANCEL_MODAL: {
            return { ...state, cancel_modal: !state.cancel_modal };
        }
        case TOGGLE_EXPORT: {
            return { ...state, export_list: action.payload };
        }
        case TOGGLE_EXPORT_FULL: {
            return { ...state, export_full: !state.export_full };
        }
        case TOGGLE_FILTERS: {
            return { ...state, filter_open: !state.filter_open };
        }
        case TOGGLE_REFRESH: {
            return { ...state, refresh: !state.refresh };
        }
        case TOGGLE_CIRCUIT_PICTURES_ORDER: {
            return { ...state, circuit_pictures_order: !state.circuit_pictures_order };
        }
        case UPDATE_CIRCUIT_PICTURE_ORDER: {
            const { drag_index, hover_index } = action.payload;
            const circuit_pictures = [...state.circuit_pictures];
            circuit_pictures.splice(drag_index, 1);
            circuit_pictures.splice(hover_index, 0, state.circuit_pictures[drag_index]);
            return { ...state, circuit_pictures: circuit_pictures };
        }
        case UPDATE_PRODUCTS: {
            return {
                ...state,
                accomoDation: state.accomoDation?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                transfers: state.transfers?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                customProducts: state.customProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                manualProducts: state.manualProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                trains: state.trains?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                car: state.car?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
                flight: state.flight?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            deletion_restriction: action.payload.lock
                        };
                    }
                    return item;
                }) ?? [],
            };
        }
        case UPDATE_PRODUCTS_STACK: {
            return {
                ...state,
                accomoDation: state.accomoDation?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                transfers: state.transfers?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                customProducts: state.customProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                manualProducts: state.manualProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                car: state.car?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
                flight: state.flight?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_stack_price: action.payload.is_stack_price
                        };
                    }
                    return item;
                }) ?? [],
            };
        }
        case UPDATE_PRODUCTS_REFERENCE_PRODUCT_TO_REPLACE: {
            return {
                ...state,
                accomoDation: state.accomoDation?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_reference_product_to_replace: action.payload.is_reference_product_to_replace
                        };
                    }
                    return item;
                }) ?? [],
                transfers: state.transfers?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_reference_product_to_replace: action.payload.is_reference_product_to_replace
                        };
                    }
                    return item;
                }) ?? [],
                customProducts: state.customProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_reference_product_to_replace: action.payload.is_reference_product_to_replace
                        };
                    }
                    return item;
                }) ?? [],
                manualProducts: state.manualProducts?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_reference_product_to_replace: action.payload.is_reference_product_to_replace
                        };
                    }
                    return item;
                }) ?? [],
                car: state.car?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_reference_product_to_replace: action.payload.is_reference_product_to_replace
                        };
                    }
                    return item;
                }) ?? [],
                flight: state.flight?.map((item) => {
                    if (action.payload.ids.includes(item.id)) {
                        return {
                            ...item,
                            is_reference_product_to_replace: action.payload.is_reference_product_to_replace
                        };
                    }
                    return item;
                }) ?? [],
            };
        }
        case UPDATE_PERIOD_START_DATE: {
            const { value, price_index, period_index } = action.payload;
            return updateCircuitPeriodProperty(value, price_index, period_index, state, 'start_date');
        }
        case UPDATE_PERIOD_END_DATE: {
            const { value, price_index, period_index } = action.payload;
            return updateCircuitPeriodProperty(value, price_index, period_index, state, 'end_date');
        }
        case UPDATE_PERIOD_DAYS: {
            const { value, price_index, period_index } = action.payload;
            return updateCircuitPeriodProperty(value, price_index, period_index, state, 'start_days');
        }
        case DELETE_CIRCUIT_PERIOD: {
            const { price_index, period_index } = action.payload;
            const room_prices = [...state.prices_master];
            const price = { ...room_prices[price_index] };
            console.log('delete circuit period: ', room_prices);
            const periods = [...price.periods];
            periods.splice(period_index, 1);
            price.periods = periods;
            room_prices[price_index] = price;
            return { ...state, prices_master: room_prices };
        }
        case SET_DUPLICATE_CIRCUIT: {
            return { ...state, duplicate_circuit: action.payload };
        }
        case DELETE_CIRCUIT_PRICE: {
            const room_prices = [...state.prices_master];
            room_prices.splice(action.payload, 1);
            return { ...state, prices_master: room_prices };
        }
        case UPDATE_PRICE_TYPE: {
            return { ...state, forfait_price: action.payload };
        }
        case UPDATE_RETURN_FLIGHT: {
            return { ...state, flight_included: action.payload };
        }
        case UPDATE_FLIGHT_PROPOSED: {
            return { ...state, flight_proposed: action.payload };
        }
        case UPDATE_FLIGHT_PRICE_INDICATIVE: {
            return { ...state, flight_price_indicative: action.payload };
        }
        case UPDATE_CONTRACT_SELECTED: {
            return { ...state, contract_selected: action.payload, extra_list: [] };
        }
        case UPDATE_CIRCUIT_NAME_LOCALE: {
            const { id, value } = action.payload;
            const circuit_name_locales = { ...state.circuit_name_locales };
            circuit_name_locales[id] = value;
            return { ...state, circuit_name_locales: circuit_name_locales };
        }
        case UPDATE_CIRCUIT_NAME_LOCALES: {
            return { ...state, circuit_name_locales: action.payload };
        }
        case UPDATE_FILTER_CATEGORIES: {
            return { ...state, filter_categories: action.payload };
        }
        case UPDATE_FILTER_CMS: {
            return { ...state, filter_cms: action.payload };
        }
        case UPDATE_FILTER_COUNTRY: {
            return { ...state, filter_country: action.payload };
        }
        case UPDATE_FILTER_CREATED_DATE_GTE: {
            return { ...state, filter_created_date_gte: action.payload };
        }
        case UPDATE_FILTER_CREATED_DATE_LTE: {
            return { ...state, filter_created_date_lte: action.payload };
        }
        case UPDATE_FILTER_CREATOR: {
            return { ...state, filter_creator: action.payload };
        }
        case UPDATE_FILTER_DESTINATION: {
            return { ...state, filter_destination: action.payload };
        }
        case UPDATE_FILTER_SOURCE: {
            return { ...state, filter_sources: action.payload };
        }
        case UPDATE_FILTER_PROVIDERS: {
            return { ...state, filter_providers: action.payload };
        }
        case UPDATE_FILTER_LANGUAGES: {
            return { ...state, filter_languages: action.payload };
        }
        case UPDATE_FILTER_LAST_AUTHOR: {
            return { ...state, filter_last_author: action.payload };
        }
        case UPDATE_FILTER_REFERENCE: {
            return { ...state, filter_reference: action.payload };
        }
        case UPDATE_FILTER_NB_OF_ISLAND_INCLUDED: {
            return { ...state, filter_nb_of_islands_included: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_GTE: {
            return { ...state, filter_modified_date_gte: action.payload };
        }
        case UPDATE_FILTER_MODIFIED_DATE_LTE: {
            return { ...state, filter_modified_date_lte: action.payload };
        }
        case UPDATE_FILTER_PERIOD: {
            const period = action.payload;
            return {
                ...state,
                filter_period: period,
                filter_period_type: 0,
                filter_created_date_gte: period === 'MODIFICATION' ? null : state.filter_created_date_gte,
                filter_created_date_lte: period === 'MODIFICATION' ? null : state.filter_created_date_lte,
                filter_modified_date_gte: period === 'CREATION' ? null : state.filter_modified_date_gte,
                filter_modified_date_lte: period === 'CREATION' ? null : state.filter_modified_date_lte,
            };
        }
        case UPDATE_FILTER_PERIOD_TYPE: {
            return { ...state, filter_period_type: action.payload };
        }
        case UPDATE_FILTER_STATUS: {
            return { ...state, filter_status: action.payload };
        }
        case UPDATE_FILTER_TYPE: {
            return { ...state, filter_type: action.payload };
        }
        case UPDATE_FILTER_PACKAGE_TYPES: {
            return { ...state, filter_package_types: action.payload };
        }
        case UPDATE_FILTER_IS_PROTECTED_DESCRIPTION: {
            return { ...state, filter_is_protected_description: action.payload };
        }
        case UPDATE_DESTINATION: {
            return { ...state, destination: action.payload };
        }
        case UPDATE_TRIP: {
            return { ...state, circuit: state.circuit ? { ...state.circuit, trip: action.payload } : null };
        }
        case UPDATE_TYPE: {
            return { ...state, type: action.payload };
        }
        case UPDATE_REFERENCE: {
            return { ...state, reference: action.payload };
        }
        case UPDATE_TRANSPORT_IN_ARRIVAL_DATE: {
            return { ...state, transport_in_arrival_date: action.payload };
        }
        case UPDATE_EXTERNAL_REFERENCE: {
            return { ...state, external_reference: action.payload };
        }
        case SET_IS_PROTECTED_DESCRIPTION: {
            return { ...state, is_protected_description: action.payload };
        }
        case UPDATE_DISPLAY_FOR_PACKAGE: {
            return { ...state, display_for_package: action.payload };
        }
        case UPDATE_DISPLAY_FOR_TRIP_PLAN: {
            return { ...state, display_for_trip_plan: action.payload };
        }
        case UPDATE_PROVIDER: {
            return { ...state, contract_provider: action.payload, contract_contract: null, contract_contract_error: '' };
        }
        case UPDATE_CONTRACT: {
            return { ...state, contract_contract: action.payload, contract_contract_error: '' };
        }
        case UPDATE_CATEGORIES: {
            return { ...state, categories: action.payload };
        }
        case UPDATE_DURATION_DAYS: {
            return { ...state, duration_days: action.payload };
        }
        case UPDATE_DURATION_NIGHTS: {
            return { ...state, duration_nights: action.payload };
        }
        case UPDATE_NB_OF_ISLANDS_INCLUDED: {
            return { ...state, nb_of_islands_included: action.payload };
        }
        case UPDATE_SALE_PITCH_LOCALE: {
            const { id, value } = action.payload;
            const sale_pitch_locales = { ...state.sale_pitch_locales };
            sale_pitch_locales[id] = value;
            return { ...state, sale_pitch_locales: sale_pitch_locales };
        }
        case UPDATE_LIST_ITEM_VISIBILITY: {
            if (state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === action.payload.id) {
                        list[i].visibility = action.payload.visibility;
                        break;
                    }
                }
                return { ...state, list: list };
            }
            return state;
        }
        case UPDATE_LIST_ITEM_PROTECTED_DESCRIPTION: {
            if (state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === action.payload.id) {
                        list[i].is_protected_description = action.payload.is_protected_description;
                        break;
                    }
                }
                return { ...state, list: list };
            }
            return state;
        }
        case UPDATE_SELECTED_CIRCUIT: {
            return { ...state, selected_circuit: action.payload };
        }
        case UPDATE_MANY_CIRCUIT_VISIBILITY: {
            return { ...state, list: state.list?.map(circuit => action.payload.circuits_id.includes(circuit.id) ? { ...circuit, visibility: action.payload.visibility } : circuit) ?? null };
        }
        default:
            return state;
    }
};

export type InputId = number | GeneratedId

type GeneratedId = `new-${number}`

export type PriceRangePeriod = Omit<PricePeriod, 'id' | 'variant' | 'for_promo_only' | 'contract' | 'trip_version'> & {
    id: InputId,
}

export type PriceRangeTerrestrialPrice = Omit<TerrestrialPrice, 'id' | 'periods' | 'contract'> & {
    id: InputId,
}

export type PriceRangeFlightPrice = Omit<FlightPrice, 'id' | 'periods' | 'contract'> & {
    id: InputId,
}

export type PriceRangePromotion = Omit<Promotion, 'id' | 'periods' | 'contract'> & {
    id: InputId
}

export type PriceRange = {
    id: InputId,
    periods: PriceRangePeriod[],
    terrestrialPrices: PriceRangeTerrestrialPrice[],
    flightPrices: PriceRangeFlightPrice[],
    promotions: PriceRangePromotion[]
}

export type PricesTablePeriod = {
    id: InputId,
    startDate: string | Date | null,
    endDate: string | Date | null,
    start_days: Day[],
    startDateError?: string,
    endDateError?: string,
}

export type PricesTableAirportsList = {
    id: InputId,
    airports: Airport[]
}

export type PricesTableItemPrice = {
    id: InputId,
    periodId: InputId,
    airportsListId?: InputId,
    terrestrialPrice: string,
    flightPrice: string
}

export type ContractPrices = {
    contracts_periods: PricePeriod[];
    contracts_prices_terrestrial: TerrestrialPrice[];
    contracts_prices_flight: FlightPrice[];
    contracts_promos: Promotion[];
}

export type VersionInfo = CircuitSliceState['pricesEdit'][`${number}-${number}`]['inputs'][0]['versionInfo']

export type CircuitSliceState = {
    first_preference_loaded: boolean,
    flight_params_list: FlightParams[],
    flight_params_list_configuration: FlightParamsListConfiguration,
    flight_params_list_loading: boolean,
    flight_params_list_count: number,
    flight_params_list_criteria: FlightParamsListCriteria,
    flight_params: FlightParams | null,
    refresh_flight_params: boolean,
    edit_flight_params_step: string,
    departure_airports_list: DepartureAirportsParams[],
    departure_airports_list_configuration: FlightParamsListConfiguration,
    departure_airports_list_loading: boolean,
    departure_airports_list_count: number,
    departure_airports_list_criteria: FlightParamsListCriteria,
    departure_airports_params: DepartureAirportsParams | null,
    refresh_departure_airports: boolean,
    edit_departure_airports_step: string,
    room_list: CircuitRoom[],
    room_list_configuration: CircuitRoomListConfiguration,
    room_list_loading: boolean,
    room_list_count: number,
    room_list_criteria: CircuitRoomListCriteria,
    room: CircuitRoom | null,
    refresh_room: boolean,
    edit_room_step: string,
    meal_plan_list: CircuitMealPlan[],
    meal_plan_list_configuration: CircuitMealPlanListConfiguration,
    meal_plan_list_loading: boolean,
    meal_plan_list_count: number,
    meal_plan_list_criteria: CircuitMealPlanListCriteria,
    meal_plan: CircuitMealPlan | null,
    refresh_meal_plan: boolean,
    edit_meal_plan_step: string,
    margins: Margin[],
    margins_configuration: CircuitMarginListConfiguration,
    margins_loading: boolean,
    margins_count: number,
    margins_criteria: MarginListCriteria,
    margin: Margin | null,
    suspended_margin: Margin | null,
    pricesEdit: {
        // circuitId-contractId
        [key: `${number}-${number}`]: {
            state: 'loading' | 'success' | 'error',
            parameters: {
                datesMode: PricesParametersDatesMode,
                datesSelection: PricesParametersDatesSelection,
            },
            inputs: {
                versionInfo: { version: number, variant: number },
                priceRanges: PriceRange[]
            }[],
            periods: PricePeriod[],
            terrestrialPrices: TerrestrialPrice[],
            flightPrices: FlightPrice[],
            modelPrices: ModelPrice[],
            promotions: Promotion[]
        }
    },
    pricesTableEdit: {
        // circuitId-contractId
        [key: `${number}-${number}`]: {
            state: 'loading' | 'success' | 'error',
            parameters: {
                datesMode: PricesParametersDatesMode,
                datesSelection: PricesParametersDatesSelection,
            },
            inputs: {
                versionInfo: VersionInfo,
                periods: PricesTablePeriod[],
                airportsList: PricesTableAirportsList[],
                prices: PricesTableItemPrice[]
            }[],
            table: {
                id: number,
                contract: Contract | null,
                airports: Airport[],
                price_xml: string,
                price_xml_purchase: string,
                price_flight: string,
                price_flight_purchase: string,
                start_date: string,
                end_date: string,
                start_days: Day[],
                trip_version: number,
                variant: number,
                can_be_modified?: boolean,
            }[]
        }
    },
    itinerary: {
        [circuit: number]: {
            state: 'loading'
        } | {
            state: 'success',
            data: {
                [version: number]: Itinerary[]
            }
        } | {
            state: 'error',
            error: Error
        }
    },
    itineraryByDay: {
        [circuit: number]: {
            state: 'loading'
        } | {
            state: 'success',
            data: ItineraryByDay[]
        } | {
            state: 'error',
            error: Error
        }
    },
    products: ListNetworkData,
    circuit_locale: number,
    currency_list: Currency[]
    cancelationRules: ListNetworkData,
    globalCancelationRules: ListNetworkData
    refresh: boolean;
    circuit_info_errors: CircuitInfoErrors;
    contractsPrices: ContractPrices;
    contract_data: CircuitContractParameters | null;
    circuit_dates: CircuitDatesForm[];
    update_stock: { date: string; block: CircuitCalendar['calendar_blocks'][number]; } | null;
    update_stock_confirmation: { date: string; block: CircuitCalendar['calendar_blocks'][number]; } | null;
    mark_as_special: boolean;
    pictures: Picture[];
    vat: number;
    departure_airports: DepartureAirport[];
    margin_master_markup: boolean;
    product_to_replace: Product | null;
    allowed_cms: Cms[];
    special_offer_list: SpecialOffer[];
    special_offer_list_configuration: SpecialOfferListConfiguration;
    special_offer_list_count: number;
    special_offer_list_loading: boolean;
    special_offer_list_criteria: SpecialOfferListCriteria;
    special_offer_refresh: boolean;
    special_offer: SpecialOffer | null;
    circuit_allowed_providers: any[];

    // rooming list
    rooming_list_export_criteria: CircuitRoomingListExportCriteria;
    circuit_ids: number[];
    rooming_list_generation: RoomingListGeneration[];
    rooming_list_generation_loaded: boolean;
    rooming_list_loading: boolean;
    rooming_list_selected_template: RoomingListParams | null;

    rooming_list_history: RoomingListHistory[];
    rooming_list_history_loaded: boolean;
    rooming_list_history_criteria: RoominglistHistoryCriteria;
    rooming_list_history_search: string;

    product_replacement_date: { date: Date | null, end_date: Date | null, dates: Date[], version?: number, variant?: number };
    trips_to_replace_selected: CircuitTripProductToReplace[],
    product_history: CircuitTripProductToReplace['products'][0] | null,
    is_price_by_room: boolean,
    is_editing_stock: boolean,

    openCopyModal: boolean,

    all_blackouts: any[],
    all_blackouts_status: 'not-loaded' | 'loaded',
    actions_panel_open: boolean,
}

const circuitSliceInitialValue: CircuitSliceState = {
    flight_params_list: [],
    flight_params_list_configuration: EMPTY_FLIGHT_PARAMS_LIST_CONFIGURATION,
    flight_params_list_loading: false,
    flight_params_list_count: 0,
    flight_params_list_criteria: EMPTY_FLIGHT_PARAMS_LIST_CRITERIA,
    flight_params: null,
    refresh_flight_params: false,
    edit_flight_params_step: '',
    departure_airports_list: [],
    departure_airports_list_configuration: EMPTY_FLIGHT_PARAMS_LIST_CONFIGURATION,
    departure_airports_list_loading: false,
    departure_airports_list_count: 0,
    departure_airports_list_criteria: EMPTY_FLIGHT_PARAMS_LIST_CRITERIA,
    departure_airports_params: null,
    refresh_departure_airports: false,
    edit_departure_airports_step: '',
    room_list: [],
    room_list_configuration: EMPTY_ROOM_LIST_CONFIGURATION,
    room_list_loading: false,
    room_list_count: 0,
    room_list_criteria: EMPTY_ROOM_LIST_CRITERIA,
    room: null,
    refresh_room: false,
    edit_room_step: '',
    meal_plan_list: [],
    meal_plan_list_configuration: EMPTY_MEAL_PLAN_LIST_CONFIGURATION,
    meal_plan_list_loading: false,
    meal_plan_list_count: 0,
    meal_plan_list_criteria: EMPTY_MEAL_PLAN_LIST_CRITERIA,
    meal_plan: null,
    refresh_meal_plan: false,
    edit_meal_plan_step: '',
    margins: [],
    margins_configuration: EMPTY_CIRCUIT_MARGIN_LIST_CONFIGURATION,
    margins_loading: false,
    margins_count: 0,
    margins_criteria: EMPTY_CIRCUIT_MARGIN_LIST_CRITERIA,
    margin: null,
    suspended_margin: null,
    pricesEdit: {},
    pricesTableEdit: {},
    itinerary: {},
    itineraryByDay: {},
    products: { state: 'loading' },
    circuit_locale: 0,
    currency_list: [],
    cancelationRules: { state: 'loading' },
    globalCancelationRules: { state: 'loading' },
    refresh: false,
    first_preference_loaded: false,
    circuit_info_errors: EMPTY_CIRCUIT_INFO_ERRORS,
    contractsPrices: {
        contracts_periods: [],
        contracts_prices_terrestrial: [],
        contracts_prices_flight: [],
        contracts_promos: [],
    },
    contract_data: null,
    circuit_dates: [],
    update_stock: null,
    update_stock_confirmation: null,
    mark_as_special: false,
    pictures: [],
    vat: 0,
    margin_master_markup: false,
    departure_airports: [],
    product_to_replace: null,
    allowed_cms: [],
    special_offer_list: [],
    special_offer_list_configuration: EMPTY_SPECIAL_OFFER_LIST_CONFIGURATION,
    special_offer_list_count: 0,
    special_offer_list_loading: false,
    special_offer_list_criteria: EMPTY_SPECIAL_OFFER_LIST_CRITERIA,
    special_offer_refresh: false,
    special_offer: null,
    circuit_allowed_providers: [],

    rooming_list_export_criteria: EMPTY_CIRCUIT_ROOMING_LIST_EXPORT_CRITERIA,
    circuit_ids: [],
    rooming_list_generation: [],
    rooming_list_generation_loaded: false,
    rooming_list_loading: false,
    rooming_list_selected_template: null,

    rooming_list_history: [],
    rooming_list_history_loaded: false,
    rooming_list_history_search: "",
    rooming_list_history_criteria: EMPTY_ROOMING_LIST_HISOTRY_CRITERIA,

    product_replacement_date: { date: null, end_date: null, dates: [] },
    trips_to_replace_selected: [],
    product_history: null,
    is_price_by_room: false,
    is_editing_stock: false,
    openCopyModal: false,

    all_blackouts: [],
    all_blackouts_status: 'not-loaded',
    actions_panel_open: true,
};


export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const setPicture = createAction<boolean | Picture>(SET_PICTURE);
const addPictures = createAction<Picture[]>(ADD_CUSTOM_PICTURES);

const updateCircuitInfoErrorsReducer = (state: CircuitSliceState, action: PayloadAction<CircuitInfoErrors>): CircuitSliceState => ({
    ...state, circuit_info_errors: action.payload
});

const setPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => {
    const { preferences, columns, order_by, order } = setFlightParamsPreference(action.payload, state.flight_params_list_configuration.columns);
    const departure_airports_preference = setDepartureAirportsPreference(action.payload, state.departure_airports_list_configuration.columns);
    const room_preference = setRoomPreference(action.payload, state.room_list_configuration.columns);
    const meal_plan_preference = setMealPlanPreference(action.payload, state.meal_plan_list_configuration.columns);
    const special_offer_preferences = setSpecialOfferPreference(action.payload, state.special_offer_list_configuration.columns);
    let margin_preference = {};
    if (action.payload.some(value => value.module === CIRCUIT_MARGIN_MODULE)) {
        const margins = setCircuitMarginPreference(action.payload, state.margins_configuration.columns);
        margin_preference = {
            margins_configuration: { ...state.margins_configuration, preferences: margins.margins_preferences, columns: margins.margins_columns },
            margins_criteria: { ...state.margins_criteria, order: margins.margins_order, order_by: margins.margins_order_by }
        };
    }
    return {
        ...state,
        first_preference_loaded: true,
        flight_params_list_configuration: { ...state.flight_params_list_configuration, preferences, columns },
        flight_params_list_criteria: { ...state.flight_params_list_criteria, order, order_by },
        departure_airports_list_configuration: { ...state.departure_airports_list_configuration, preferences: departure_airports_preference.preferences, columns: departure_airports_preference.columns },
        departure_airports_list_criteria: { ...state.departure_airports_list_criteria, order: departure_airports_preference.order, order_by: departure_airports_preference.order_by },
        room_list_configuration: { ...state.room_list_configuration, preferences: room_preference.preferences, columns: room_preference.columns },
        room_list_criteria: { ...state.room_list_criteria, order: room_preference.order, order_by: room_preference.order_by },
        meal_plan_list_configuration: { ...state.meal_plan_list_configuration, preferences: meal_plan_preference.preferences, columns: meal_plan_preference.columns },
        meal_plan_list_criteria: { ...state.meal_plan_list_criteria, order: meal_plan_preference.order, order_by: meal_plan_preference.order_by },
        special_offer_list_configuration: { ...state.special_offer_list_configuration, preferences: special_offer_preferences.preferences, columns: special_offer_preferences.columns },
        special_offer_list_criteria: { ...state.special_offer_list_criteria, order: special_offer_preferences.order, order_by: special_offer_preferences.order_by },
        ...margin_preference
    };
};

// ------------------ flight_params ------------------ //
const addFlightParamsListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, preferences: [...state.flight_params_list_configuration.preferences, ...action.payload] }
});

const setFlightParamsListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, preferences: action.payload }
});

const setColumnsReducer = (state: CircuitSliceState, action: PayloadAction<Column[]>): CircuitSliceState => ({
    ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, columns: action.payload },
});

const toggleColumnReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => {
    const columns = cloneDeep([...state.flight_params_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, flight_params_list_configuration: { ...state.flight_params_list_configuration, columns } };
};
const updateFlightParamsListReducer = (state: CircuitSliceState, action: PayloadAction<FlightParams[]>): CircuitSliceState => ({
    ...state, flight_params_list: action.payload.map(params => ({
        ...params,
        providers: params.providers || [],
        allowed_client_type: params.allowed_client_type || [],
        allowed_airline: params.allowed_airline || [],
        disallowed_airline: params.disallowed_airline || [],
        number_of_bags: params.number_of_bags || [],
        allowed_stopovers_airport: params.allowed_stopovers_airport || [],
        disallowed_stopovers_airport: params.disallowed_stopovers_airport || [],
        allowed_departure_airport: params.allowed_departure_airport || [],
        disallowed_departure_airport: params.disallowed_departure_airport || [],
        allowed_arrival_airport: params.allowed_arrival_airport || [],
        disallowed_arrival_airport: params.disallowed_arrival_airport || [],
        allowed_return_airport: params.allowed_return_airport || null,
        period: params.period || [],
        departure_dow: params.departure_dow || [],
        without_transport_arrival_dow: params.without_transport_arrival_dow || [],
        arrival_dow: params.arrival_dow || [],
        cabin: params.cabin || [],
        tariff_letters: params.tariff_letters || []
    }))
});
const updateFlightParamsListCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<FlightParamsListCriteria>): CircuitSliceState => ({
    ...state, flight_params_list_criteria: action.payload
});
const updateFlightParamsReducer = (state: CircuitSliceState, action: PayloadAction<FlightParams>): CircuitSliceState => ({
    ...state,
    flight_params: {
        ...action.payload,
        assigned_trip: action.payload.assigned_trip ?? 'CIRCUIT_PACKAGES',
        max_stops: action.payload.max_stops ?? '',
        max_hour_spent_on_stopover: action.payload.max_hour_spent_on_stopover ?? '',
        start_arrival_time: action.payload.start_arrival_time ?? '',
        end_arrival_time: action.payload.end_arrival_time ?? '',
        start_departure_time: action.payload.start_departure_time ?? '',
        end_departure_time: action.payload.end_departure_time ?? '',
        start_arrival_time_inbound: action.payload.start_arrival_time_inbound ?? '',
        end_arrival_time_inbound: action.payload.end_arrival_time_inbound ?? '',
        start_departure_time_inbound: action.payload.start_departure_time_inbound ?? '',
        end_departure_time_inbound: action.payload.end_departure_time_inbound ?? '',
        allow_number_of_allotment: Boolean(action.payload.allow_number_of_allotment),
        flight_duration_min: action.payload.flight_duration_min ?? '',
        flight_duration_max: action.payload.flight_duration_max ?? '',
        allowed_client_type: action.payload.allowed_client_type ?? [],
        period: action.payload.period.map((period) => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })),
        providers: action.payload.providers || [],
        allowed_airline: action.payload.allowed_airline || [],
        disallowed_airline: action.payload.disallowed_airline || [],
        number_of_bags: action.payload.number_of_bags || [],
        allowed_stopovers_airport: action.payload.allowed_stopovers_airport || [],
        disallowed_stopovers_airport: action.payload.disallowed_stopovers_airport || [],
        allowed_departure_airport: action.payload.allowed_departure_airport || [],
        disallowed_departure_airport: action.payload.disallowed_departure_airport || [],
        allowed_arrival_airport: action.payload.allowed_arrival_airport || [],
        disallowed_arrival_airport: action.payload.disallowed_arrival_airport || [],
        allowed_return_airport: action.payload.allowed_return_airport || null,
        departure_dow: action.payload.departure_dow || [],
        without_transport_arrival_dow: action.payload.without_transport_arrival_dow || [],
        arrival_dow: action.payload.arrival_dow || [],
        cabin: action.payload.cabin || [],
        tariff_letters: action.payload.tariff_letters || []
    }
});
const updateFlightParamsListLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, flight_params_list_loading: action.payload
});
const updateFlightParamsListCountReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => ({
    ...state, flight_params_list_count: action.payload
});



// ------------------ departure_airports ------------------ //
const addDepartureAirportsListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, departure_airports_list_configuration: { ...state.departure_airports_list_configuration, preferences: [...state.departure_airports_list_configuration.preferences, ...action.payload] }
});

const setDepartureAirportsListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, departure_airports_list_configuration: { ...state.departure_airports_list_configuration, preferences: action.payload }
});

const setDepartureAirportsColumnsReducer = (state: CircuitSliceState, action: PayloadAction<Column[]>): CircuitSliceState => ({
    ...state, departure_airports_list_configuration: { ...state.departure_airports_list_configuration, columns: action.payload },
});

const toggleDepartureAirportsColumnReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => {
    const columns = cloneDeep([...state.departure_airports_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, departure_airports_list_configuration: { ...state.departure_airports_list_configuration, columns } };
};

const updateDepartureAirportsListReducer = (state: CircuitSliceState, action: PayloadAction<DepartureAirportsParams[]>): CircuitSliceState => ({
    ...state, departure_airports_list: action.payload.map(params => ({
        ...params,
        providers: params.providers || [],
        allowed_client_type: params.allowed_client_type || [],
        allowed_airline: params.allowed_airline || [],
        disallowed_airline: params.disallowed_airline || [],
        number_of_bags: params.number_of_bags || [],
        allowed_stopovers_airport: params.allowed_stopovers_airport || [],
        disallowed_stopovers_airport: params.disallowed_stopovers_airport || [],
        allowed_departure_airport: params.allowed_departure_airport || [],
        disallowed_departure_airport: params.disallowed_departure_airport || [],
        allowed_arrival_airport: params.allowed_arrival_airport || [],
        disallowed_arrival_airport: params.disallowed_arrival_airport || [],
        allowed_return_airport: params.allowed_return_airport || null,
        period: params.period || [],
        departure_dow: params.departure_dow || [],
        arrival_dow: params.arrival_dow || [],
        cabin: params.cabin || [],
        tariff_letters: params.tariff_letters || []
    }))
});
const updateDepartureAirportsListCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<FlightParamsListCriteria>): CircuitSliceState => ({
    ...state, departure_airports_list_criteria: action.payload
});
const updateDepartureAirportsReducer = (state: CircuitSliceState, action: PayloadAction<DepartureAirportsParams>): CircuitSliceState => ({
    ...state,
    departure_airports_params: {
        ...action.payload,
        assigned_trip: action.payload.assigned_trip ?? 'CIRCUIT_PACKAGES',
        max_stops: action.payload.max_stops ?? '',
        max_hour_spent_on_stopover: action.payload.max_hour_spent_on_stopover ?? '',
        start_arrival_time: action.payload.start_arrival_time ?? '',
        end_arrival_time: action.payload.end_arrival_time ?? '',
        start_departure_time: action.payload.start_departure_time ?? '',
        end_departure_time: action.payload.end_departure_time ?? '',
        start_arrival_time_inbound: action.payload.start_arrival_time_inbound ?? '',
        end_arrival_time_inbound: action.payload.end_arrival_time_inbound ?? '',
        start_departure_time_inbound: action.payload.start_departure_time_inbound ?? '',
        end_departure_time_inbound: action.payload.end_departure_time_inbound ?? '',
        allow_number_of_allotment: Boolean(action.payload.allow_number_of_allotment),
        flight_duration_min: action.payload.flight_duration_min ?? '',
        flight_duration_max: action.payload.flight_duration_max ?? '',
        allowed_client_type: action.payload.allowed_client_type ?? [],
        period: action.payload.period?.map((period) => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) ?? [],
        providers: action.payload.providers || [],
        allowed_airline: action.payload.allowed_airline || [],
        disallowed_airline: action.payload.disallowed_airline || [],
        number_of_bags: action.payload.number_of_bags || [],
        allowed_stopovers_airport: action.payload.allowed_stopovers_airport || [],
        disallowed_stopovers_airport: action.payload.disallowed_stopovers_airport || [],
        allowed_departure_airport: action.payload.allowed_departure_airport || [],
        disallowed_departure_airport: action.payload.disallowed_departure_airport || [],
        allowed_arrival_airport: action.payload.allowed_arrival_airport || [],
        disallowed_arrival_airport: action.payload.disallowed_arrival_airport || [],
        allowed_return_airport: action.payload.allowed_return_airport || null,
        departure_dow: action.payload.departure_dow || [],
        arrival_dow: action.payload.arrival_dow || [],
        cabin: action.payload.cabin || [],
        tariff_letters: action.payload.tariff_letters || []
    }
});
const updateDepartureAirportsListLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, departure_airports_list_loading: action.payload
});
const updateDepartureAirportsListCountReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => ({
    ...state, departure_airports_list_count: action.payload
});



// ------------------ room ------------------ //
const addRoomListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, room_list_configuration: { ...state.room_list_configuration, preferences: [...state.room_list_configuration.preferences, ...action.payload] }
});

const setRoomListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, room_list_configuration: { ...state.room_list_configuration, preferences: action.payload }
});

const setRoomColumnsReducer = (state: CircuitSliceState, action: PayloadAction<Column[]>): CircuitSliceState => ({
    ...state, room_list_configuration: { ...state.room_list_configuration, columns: action.payload },
});

const toggleRoomColumnReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => {
    const columns = cloneDeep([...state.room_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, room_list_configuration: { ...state.room_list_configuration, columns } };
};

const updateRoomListReducer = (state: CircuitSliceState, action: PayloadAction<CircuitRoom[]>): CircuitSliceState => ({
    ...state, room_list: action.payload.map(params => ({
        ...params,
    }))
});
const updateRoomListCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<FlightParamsListCriteria>): CircuitSliceState => ({
    ...state, room_list_criteria: action.payload
});
const updateRoomReducer = (state: CircuitSliceState, action: PayloadAction<CircuitRoom>): CircuitSliceState => ({
    ...state,
    room: {
        ...action.payload,
    }
});
const updateRoomListLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, room_list_loading: action.payload
});
const updateRoomListCountReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => ({
    ...state, room_list_count: action.payload
});


// ------------------ meal_plan ------------------ //
const addMealPlanListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, meal_plan_list_configuration: { ...state.meal_plan_list_configuration, preferences: [...state.meal_plan_list_configuration.preferences, ...action.payload] }
});

const setMealPlanListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, meal_plan_list_configuration: { ...state.meal_plan_list_configuration, preferences: action.payload }
});

const setMealPlanColumnsReducer = (state: CircuitSliceState, action: PayloadAction<Column[]>): CircuitSliceState => ({
    ...state, meal_plan_list_configuration: { ...state.meal_plan_list_configuration, columns: action.payload },
});

const toggleMealPlanColumnReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => {
    const columns = cloneDeep([...state.meal_plan_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, meal_plan_list_configuration: { ...state.meal_plan_list_configuration, columns } };
};

const updateMealPlanListReducer = (state: CircuitSliceState, action: PayloadAction<CircuitMealPlan[]>): CircuitSliceState => ({
    ...state, meal_plan_list: action.payload.map(params => ({
        ...params,
    }))
});
const updateMealPlanListCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<FlightParamsListCriteria>): CircuitSliceState => ({
    ...state, meal_plan_list_criteria: action.payload
});
const updateMealPlanReducer = (state: CircuitSliceState, action: PayloadAction<CircuitMealPlan>): CircuitSliceState => ({
    ...state,
    meal_plan: {
        ...action.payload,
    }
});
const updateMealPlanListLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, meal_plan_list_loading: action.payload
});
const updateMealPlanListCountReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => ({
    ...state, meal_plan_list_count: action.payload
});

// ------------------ margins ------------------ //
const setSuspendedMarginReducer = (state: CircuitSliceState, action: PayloadAction<Margin | null>): CircuitSliceState => ({
    ...state, suspended_margin: action.payload
});

const addMarginsConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: [...state.margins_configuration.preferences, ...action.payload] }
});

const setMarginsConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, margins_configuration: { ...state.margins_configuration, preferences: action.payload }
});

const toggleMarginColumnReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => {
    const columns = cloneDeep([...state.margins_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, margins_configuration: { ...state.margins_configuration, columns } };
};

const setCircuitMarginsReducer = (state: CircuitSliceState, action: PayloadAction<Margin[]>): CircuitSliceState => ({
    ...state, margins: action.payload
});
const updateCircuitMarginsCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<MarginListCriteria>): CircuitSliceState => ({
    ...state, margins_criteria: action.payload
});
const updateCircuitMarginReducer = (state: CircuitSliceState, action: PayloadAction<Margin | null>): CircuitSliceState => ({
    ...state, margin: action.payload
});
const updateCircuitMarginsLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, margins_loading: action.payload
});
const updateCircuitMarginsCountReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => ({
    ...state, margins_count: action.payload
});

const setMarginColumnsReducer = (state: CircuitSliceState, action: PayloadAction<Column[]>): CircuitSliceState => ({
    ...state, margins_configuration: { ...state.margins_configuration, columns: action.payload },
});



const setContractDataReducer = (state: CircuitSliceState, action: PayloadAction<CircuitContractParameters | null>): CircuitSliceState => ({
    ...state, contract_data: action.payload
});
const updateCircuitDatesReducer = (state: CircuitSliceState, action: PayloadAction<CircuitDatesForm[]>): CircuitSliceState => ({
    ...state, circuit_dates: action.payload
});
const setUpdateStockReducer = (state: CircuitSliceState, action: PayloadAction<{ date: string; block: CircuitCalendar['calendar_blocks'][number]; } | null>): CircuitSliceState => ({
    ...state, update_stock: action.payload
});
const setUpdateStockConfirmationReducer = (state: CircuitSliceState, action: PayloadAction<{ date: string; block: CircuitCalendar['calendar_blocks'][number]; } | null>): CircuitSliceState => ({
    ...state, update_stock_confirmation: action.payload
});

// ------- rooming list -------------- //
const updateRoomingListExportCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<CircuitRoomingListExportCriteria>): CircuitSliceState => ({
    ...state, rooming_list_export_criteria: action.payload
});
const setRoomingListLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, rooming_list_loading: action.payload
});
const updateRoomingListGenerationReducer = (state: CircuitSliceState, action: PayloadAction<RoomingListGeneration[]>): CircuitSliceState => ({
    ...state, rooming_list_generation: action.payload, rooming_list_generation_loaded: true
});
const updateRoomingListSelectedTemplateReducer = (state: CircuitSliceState, action: PayloadAction<RoomingListParams | null>): CircuitSliceState => ({
    ...state, rooming_list_selected_template: action.payload
});
const setCircuitIdsReducer = (state: CircuitSliceState, action: PayloadAction<number[]>): CircuitSliceState => ({
    ...state, circuit_ids: action.payload
});
const setRoomingListGenerationLoadedReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, rooming_list_generation_loaded: action.payload
});


// ------------------- rooming list history -------------------------- //
const setRoomingListHistoryReducer = (state: CircuitSliceState, action: PayloadAction<RoomingListHistory[]>): CircuitSliceState => ({
    ...state, rooming_list_history: action.payload, rooming_list_history_loaded: true
});
const setRoomingListHistoryCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<RoominglistHistoryCriteria>): CircuitSliceState => ({
    ...state, rooming_list_history_criteria: action.payload
});
const setRoomingListHistorySearchReducer = (state: CircuitSliceState, action: PayloadAction<string>): CircuitSliceState => ({
    ...state, rooming_list_history_search: action.payload, rooming_list_history_criteria: { ...state.rooming_list_history_criteria, page: 0 }
});
const setRoomingListHistoryLoadedReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, rooming_list_history_loaded: action.payload
});


const addPicturesReducer = (
    state: CircuitSliceState,
    action: PayloadAction<any>
): CircuitSliceState => ({
    ...state, pictures: [...state.pictures, ...action.payload]
});

const setPictureReducer = (
    state: CircuitSliceState,
    action: PayloadAction<any>
): CircuitSliceState =>
    action.payload !== true && action.payload !== false
        ? {
            ...state,
            pictures: state.pictures.map(
                (picture) =>
                    picture.id === (action.payload as Picture).id
                        ? (action.payload as Picture)
                        : picture
            )
        }
        : { ...state };


const updateAllowedCmsReducer = (state: CircuitSliceState, action: PayloadAction<Cms[]>): CircuitSliceState => ({
    ...state, allowed_cms: action.payload
});

// special offer
const addSpecialOfferListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, special_offer_list_configuration: { ...state.special_offer_list_configuration, preferences: [...state.special_offer_list_configuration.preferences, ...action.payload] }
});

const setSpecialOfferListConfigurationPreferenceReducer = (state: CircuitSliceState, action: PayloadAction<Preferences[]>): CircuitSliceState => ({
    ...state, special_offer_list_configuration: { ...state.special_offer_list_configuration, preferences: action.payload }
});
const setSpecialOfferColumnsReducer = (state: CircuitSliceState, action: PayloadAction<Column[]>): CircuitSliceState => ({
    ...state, special_offer_list_configuration: { ...state.special_offer_list_configuration, columns: action.payload },
});

const toggleSpecialOfferColumnReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => {
    const columns = cloneDeep([...state.special_offer_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, special_offer_list_configuration: { ...state.special_offer_list_configuration, columns } };
};

const updateSpecialOfferListReducer = (state: CircuitSliceState, action: PayloadAction<SpecialOffer[]>): CircuitSliceState => ({
    ...state, special_offer_list: action.payload || []
});
const updateSpecialOfferListCriteriaReducer = (state: CircuitSliceState, action: PayloadAction<SpecialOfferListCriteria>): CircuitSliceState => ({
    ...state, special_offer_list_criteria: action.payload
});
const updateSpecialOfferReducer = (state: CircuitSliceState, action: PayloadAction<SpecialOffer | null>): CircuitSliceState => ({
    ...state,
    special_offer: action.payload,
    pictures: action.payload?.picture ? [action.payload.picture] : []
    // getSpecialOfferData(response.data.results[0], Number(current_locale))
});
const updateSpecialOfferListLoadingReducer = (state: CircuitSliceState, action: PayloadAction<boolean>): CircuitSliceState => ({
    ...state, special_offer_list_loading: action.payload
});
const updateSpecialOfferListCountReducer = (state: CircuitSliceState, action: PayloadAction<number>): CircuitSliceState => ({
    ...state, special_offer_list_count: action.payload
});
const toggleSpecialOfferRefreshReducer = (state: CircuitSliceState): CircuitSliceState => ({
    ...state, special_offer_refresh: !state.special_offer_refresh
});



export const circuitSlice = createSlice({
    name: 'circuitSlice',
    initialState: circuitSliceInitialValue,
    reducers: {
        toggleRefreshFlightParams: (state: CircuitSliceState) => ({ ...state, refresh_flight_params: !state.refresh_flight_params }),
        setFlightParams: (state: CircuitSliceState, action: PayloadAction<FlightParams | null>) => ({
            ...state, flight_params: action.payload ? {
                ...action.payload,
                assigned_trip: action.payload.assigned_trip ?? 'CIRCUIT_PACKAGES',
                max_stops: action.payload.max_stops ?? '',
                max_hour_spent_on_stopover: action.payload.max_hour_spent_on_stopover ?? '',
                start_arrival_time: action.payload.start_arrival_time ?? '',
                end_arrival_time: action.payload.end_arrival_time ?? '',
                start_departure_time: action.payload.start_departure_time ?? '',
                end_departure_time: action.payload.end_departure_time ?? '',
                start_arrival_time_inbound: action.payload.start_arrival_time_inbound ?? '',
                end_arrival_time_inbound: action.payload.end_arrival_time_inbound ?? '',
                start_departure_time_inbound: action.payload.start_departure_time_inbound ?? '',
                end_departure_time_inbound: action.payload.end_departure_time_inbound ?? '',
                allow_number_of_allotment: Boolean(action.payload.allow_number_of_allotment),
                flight_duration_min: action.payload.flight_duration_min ?? '',
                flight_duration_max: action.payload.flight_duration_max ?? '',
                allowed_client_type: action.payload.allowed_client_type ?? [],
                providers: action.payload.providers || [],
                allowed_airline: action.payload.allowed_airline || [],
                disallowed_airline: action.payload.disallowed_airline || [],
                number_of_bags: action.payload.number_of_bags || [],
                allowed_stopovers_airport: action.payload.allowed_stopovers_airport || [],
                disallowed_stopovers_airport: action.payload.disallowed_stopovers_airport || [],
                allowed_departure_airport: action.payload.allowed_departure_airport || [],
                disallowed_departure_airport: action.payload.disallowed_departure_airport || [],
                allowed_arrival_airport: action.payload.allowed_arrival_airport || [],
                disallowed_arrival_airport: action.payload.disallowed_arrival_airport || [],
                allowed_return_airport: action.payload.allowed_return_airport || null,
                departure_dow: action.payload.departure_dow || [],
                without_transport_arrival_dow: action.payload.without_transport_arrival_dow || [],
                arrival_dow: action.payload.arrival_dow || [],
                cabin: action.payload.cabin || [],
                tariff_letters: action.payload.tariff_letters || [],
                period: action.payload.period?.map((period) => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null })) ?? []
            } : null
        }),
        setEditFlightParamsSetps: (state: CircuitSliceState, action: PayloadAction<string>) => ({ ...state, edit_flight_params_step: action.payload }),
        toggleRefreshDepartureAirports: (state: CircuitSliceState) => ({ ...state, refresh_departure_airports: !state.refresh_departure_airports }),
        setDepartureAirportsParams: (state: CircuitSliceState, action: PayloadAction<DepartureAirportsParams | null>) => ({
            ...state, departure_airports_params: action.payload ? {
                ...action.payload,
                assigned_trip: action.payload.assigned_trip ?? 'CIRCUIT_PACKAGES',
                max_stops: action.payload.max_stops ?? '',
                max_hour_spent_on_stopover: action.payload.max_hour_spent_on_stopover ?? '',
                start_arrival_time: action.payload.start_arrival_time ?? '',
                end_arrival_time: action.payload.end_arrival_time ?? '',
                start_departure_time: action.payload.start_departure_time ?? '',
                end_departure_time: action.payload.end_departure_time ?? '',
                start_arrival_time_inbound: action.payload.start_arrival_time_inbound ?? '',
                end_arrival_time_inbound: action.payload.end_arrival_time_inbound ?? '',
                start_departure_time_inbound: action.payload.start_departure_time_inbound ?? '',
                end_departure_time_inbound: action.payload.end_departure_time_inbound ?? '',
                allow_number_of_allotment: Boolean(action.payload.allow_number_of_allotment),
                flight_duration_min: action.payload.flight_duration_min ?? '',
                flight_duration_max: action.payload.flight_duration_max ?? '',
                allowed_client_type: action.payload.allowed_client_type ?? [],
                period: action.payload.period?.map((period) => ({ start_date: period.start_date ? new Date(period.start_date) : null, end_date: period.end_date ? new Date(period.end_date) : null }))
            } : null
        }),
        setEditDepartureAirportsSetps: (state: CircuitSliceState, action: PayloadAction<string>) => ({ ...state, edit_departure_airports_step: action.payload }),
        toggleRefreshRoom: (state: CircuitSliceState) => ({ ...state, refresh_room: !state.refresh_room }),
        setRoom: (state: CircuitSliceState, action: PayloadAction<CircuitRoom | null>) => ({
            ...state, room: action.payload ? {
                ...action.payload,
            } : null
        }),
        setEditRoomSetps: (state: CircuitSliceState, action: PayloadAction<string>) => ({ ...state, edit_room_step: action.payload }),
        toggleRefreshMealPlan: (state: CircuitSliceState) => ({ ...state, refresh_meal_plan: !state.refresh_meal_plan }),
        setMealPlan: (state: CircuitSliceState, action: PayloadAction<CircuitMealPlan | null>) => ({
            ...state, meal_plan: action.payload ? {
                ...action.payload,
            } : null
        }),
        setEditMealPlanSetps: (state: CircuitSliceState, action: PayloadAction<string>) => ({ ...state, edit_meal_plan_step: action.payload }),
        setSuspendedMargin: setSuspendedMarginReducer,
        addMarginsConfigurationPreference: addMarginsConfigurationPreferenceReducer,
        setMarginsConfigurationPreference: setMarginsConfigurationPreferenceReducer,
        toggleMarginColumn: toggleMarginColumnReducer,
        setCircuitMargins: setCircuitMarginsReducer,
        updateCircuitMarginsCriteria: updateCircuitMarginsCriteriaReducer,
        updateCircuitMargin: updateCircuitMarginReducer,
        updateCircuitMarginsLoading: updateCircuitMarginsLoadingReducer,
        updateCircuitMarginsCount: updateCircuitMarginsCountReducer,
        setMarginColumns: setMarginColumnsReducer,
        setOpenCopyModal: (state: CircuitSliceState, action: PayloadAction<boolean>) => ({ ...state, openCopyModal: action.payload }),
        toggleOpenCopyModal: (state: CircuitSliceState) => ({ ...state, openCopyModal: !state.openCopyModal }),
        addPricesEditPriceRange: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const datesMode = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.parameters.datesMode || 'FIXED';
            const today = new Date();
            let day = getDay(today) - 1;
            day = day < 0 ? 6 : day;
            if (item) {
                item.priceRanges.push({
                    id: generateId(),
                    periods: [
                        {
                            id: generateId(),
                            start_date: today.toISOString(),
                            end_date: datesMode === 'FIXED' ? today.toISOString() : addDays(today, 1).toISOString(),
                            start_days: [day]
                        }
                    ],
                    terrestrialPrices: [],
                    flightPrices: [],
                    promotions: []
                });
            } else {
                state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    priceRanges: [{
                        id: generateId(),
                        periods: [
                            {
                                id: generateId(),
                                start_date: today.toISOString(),
                                end_date: datesMode === 'FIXED' ? today.toISOString() : addDays(today, 1).toISOString(),
                                start_days: [day]
                            }
                        ],
                        terrestrialPrices: [],
                        flightPrices: [],
                        promotions: []
                    }]
                });
            }
        },
        addPricesEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                datesMode: PricesParametersDatesMode
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                const last_period = priceRange.periods[priceRange.periods.length - 1];
                const last_end_date = last_period ? new Date(last_period.end_date) : null;
                // filet de securite : une periode existante peut porter une date illisible,
                // on repart d'aujourd'hui plutot que de planter sur toISOString()
                const date = last_end_date && isValid(last_end_date) ? last_end_date : new Date();
                let day = getDay(date) - 1;
                day = day < 0 ? 6 : day;
                priceRange.periods.push({
                    id: generateId(),
                    start_date: date.toISOString(),
                    end_date: action.payload.datesMode === 'FIXED' ?
                        date.toISOString() :
                        addDays(date, 1).toISOString(),
                    start_days: [day]
                });
            }
        },
        addPricesEditTerrestrialPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                model: ModelPrice | null,
                circuit_room?: number | null
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.terrestrialPrices.push({
                    id: generateId(),
                    is_price_reference: action.payload.model ? false : true,
                    model: action.payload.model,
                    values: action.payload.model?.variable.map((variable) => ({
                        variable: variable as 'AGE' | 'NUMBER_OF_PERSONS' | 'NUMBER_OF_PERSONS_PRICE',
                        value: [0, 0]
                    })) ?? [],
                    use_room_pricing: !action.payload.model,
                    price: '',
                    selling_price: '',
                    com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    room_min_pax: '',
                    room_max_pax: '',
                    circuit_room: action.payload.circuit_room ?? null,
                    single_price: '',
                    double_price: '',
                    triple_price: '',
                    quad_price: '',
                    single_selling_price: '',
                    double_selling_price: '',
                    triple_selling_price: '',
                    quad_selling_price: '',
                    single_com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    double_com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    triple_com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    quad_com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    single_markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    double_markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    triple_markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    quad_markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    child_prices: []
                });
            }
        },
        addPricesEditFlightPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId?: InputId,
                airport?: Airport
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            const price = priceRange?.flightPrices.find((item) => item.id === action.payload.priceId);
            if (price && action.payload.airport) {
                price.airports.push(action.payload.airport);
            } else {
                priceRange?.flightPrices.push({
                    id: action.payload.priceId ?? generateId(),
                    airports: action.payload.airport ? [action.payload.airport] : [],
                    price: '0',
                    selling_price: '',
                    com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    can_be_modified: true
                });
            }
        },
        // flight prices built from the offline flights, saved read only
        addPricesEditOfflineFlightPrices: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                flightPrices: PriceRangeFlightPrice[]
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.flightPrices = priceRange.flightPrices.concat(action.payload.flightPrices);
            }
        },
        // a range per amount group of the offline flights no range sells yet
        addPricesEditOfflineFlightPriceRanges: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRanges: Pick<PriceRange, 'periods' | 'flightPrices'>[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            const priceRanges: PriceRange[] = action.payload.priceRanges.map((priceRange) => ({
                id: generateId(),
                periods: priceRange.periods,
                terrestrialPrices: [],
                flightPrices: priceRange.flightPrices,
                promotions: []
            }));
            const item = contractPrices?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.priceRanges = item.priceRanges.concat(priceRanges);
            } else {
                contractPrices?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    priceRanges
                });
            }
        },
        addPricesEditPromotion: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                model: ModelPrice,
                circuit_room?: number | null
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.promotions.push({
                    id: generateId(),
                    name: '',
                    is_price_reduction: false,
                    strict_price_model: false,
                    model: action.payload.model,
                    values: action.payload.model.variable.map((variable) => ({
                        variable: variable as 'AGE' | 'NUMBER_OF_PERSONS' | 'NUMBER_OF_PERSONS_PRICE',
                        value: [0, 0]
                    })),
                    price: '',
                    selling_price: '',
                    com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    room_min_pax: '',
                    room_max_pax: '',
                    circuit_room: action.payload.circuit_room ?? null
                });
            }
        },
        addPricesTableEditAirport: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                listId: InputId,
                airport: Airport
            }>
        ): void => {
            const list = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.airportsList.find((item) => {
                return item.id === action.payload.listId;
            });

            if (list) {
                list.airports.push(action.payload.airport);
            }
        },
        addPricesTableEditAirportsList: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.airportsList.push({
                    id: generateId(),
                    airports: []
                });
            } else {
                state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    periods: [],
                    airportsList: [{
                        id: generateId(),
                        airports: []
                    }],
                    prices: []
                });
            }
        },
        addPricesTableEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                datesMode?: PricesParametersDatesMode
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                // the latest end date, not the last pushed one, and the day after it: starting on
                // the end of another period makes every added period overlap by construction
                let last_end_date: Date | null = null;
                item.periods.forEach((period) => {
                    // toDate reads a 'yyyy-MM-dd' as a local day: new Date() would read it as UTC
                    // midnight and land on the day before in every negative offset
                    const end = toDate(period.endDate as string | Date | null);
                    if (end && (!last_end_date || end > last_end_date)) {
                        last_end_date = end;
                    }
                });
                // meme filet de securite que addPricesEditPeriod
                const date = (last_end_date ? addDays(last_end_date, 1) : new Date()).toISOString();
                const endDate = action.payload.datesMode !== 'FIXED' ? date : addDays(new Date(date), 1).toISOString();
                let day = getDay(new Date(date)) - 1;
                day = day < 0 ? 6 : day;
                item.periods.push({
                    id: generateId(),
                    startDate: date,
                    endDate: endDate,
                    start_days: [day]
                });
            } else {
                const date = new Date().toISOString();
                const endDate = action.payload.datesMode !== 'FIXED' ? date : addDays(new Date(date || ''), 1).toISOString();
                let day = getDay(new Date(date || '')) - 1;
                day = day < 0 ? 6 : day;
                state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    periods: [{
                        id: generateId(),
                        startDate: date,
                        endDate: endDate,
                        start_days: [day]
                    }],
                    airportsList: [],
                    prices: []
                });
            }
        },
        deletePricesEditPriceRange: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.priceRanges = item.priceRanges.filter((item) => item.id !== action.payload.priceRangeId);
            }
        },
        deletePricesEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                periodId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.periods = priceRange.periods.filter((item) => item.id !== action.payload.periodId).sort((a, b) => {
                    return isBefore(new Date(a.start_date), new Date(b.start_date)) ?
                        -1 :
                        1;
                });
            }
        },
        deletePricesEditTerrestrialPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.terrestrialPrices = priceRange.terrestrialPrices.filter((item) => {
                    return item.id !== action.payload.priceId;
                });
            }
        },
        deletePricesEditPromotion: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                promotionId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.promotions = priceRange.promotions.filter((item) => {
                    return item.id !== action.payload.promotionId;
                });
            }
        },
        deletePricesEditFlightPrice: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            if (priceRange) {
                priceRange.flightPrices = priceRange.flightPrices.filter((item) => {
                    return item.id !== action.payload.priceId;
                });
            }
        },
        deletePricesEditAirport: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId,
                airport: Airport
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const priceRange = item?.priceRanges.find((item) => item.id === action.payload.priceRangeId);
            const price = priceRange?.flightPrices.find((item) => item.id === action.payload.priceId);
            if (price) {
                price.airports = price.airports.filter((item) => item.id !== action.payload.airport.id);
            }
        },
        deletePricesTableEditAirport: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                listId: InputId,
                airport: Airport
            }>
        ): void => {
            const list = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.airportsList.find((item) => {
                return item.id === action.payload.listId;
            });
            if (list) {
                list.airports = list.airports.filter((item) => {
                    return item.id !== action.payload.airport.id;
                });
            }
        },
        deletePricesTableEditAirportsList: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                listId: InputId,
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.airportsList = item.airportsList.filter((item) => {
                    return item.id !== action.payload.listId;
                });
                item.prices = item.prices.filter((item) => {
                    return item.airportsListId !== action.payload.listId;
                });
            }
        },
        deletePricesTableEditPeriod: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                periodId: InputId,
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.periods = item.periods.filter((item) => {
                    return item.id !== action.payload.periodId;
                });
                item.prices = item.prices.filter((item) => {
                    return item.periodId !== action.payload.periodId;
                });
            }
        },
        setCancelationRules: (
            state,
            action: PayloadAction<CircuitSliceState['cancelationRules']>
        ): void => {
            state.cancelationRules = action.payload;
        },
        setGlobalCancelationRules: (
            state,
            action: PayloadAction<CircuitSliceState['globalCancelationRules']>
        ): void => {
            state.globalCancelationRules = action.payload;
        },
        setPricesEditValue: <K extends keyof CircuitSliceState['pricesEdit'][`${number}-${number}`]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                key: K,
                value: CircuitSliceState['pricesEdit'][`${number}-${number}`][K]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices[action.payload.key] = action.payload.value;
            } else {
                state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`] = {
                    state: 'loading',
                    parameters: {
                        datesMode: 'FREE',
                        datesSelection: 'DATE_PICKER',
                    },
                    inputs: [],
                    periods: [],
                    terrestrialPrices: [],
                    flightPrices: [],
                    modelPrices: [],
                    promotions: [],
                    [action.payload.key]: action.payload.value
                };
            }
        },
        setPricesEditParameters: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                parameters: CircuitSliceState['pricesEdit'][`${number}-${number}`]['parameters']
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.parameters = action.payload.parameters;
            }
        },
        setPricesEditPriceRanges: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRanges: PriceRange[]
            }>
        ): void => {
            const item = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            if (item) {
                item.priceRanges = action.payload.priceRanges?.map((range) => ({
                    ...range,
                    periods: [...range.periods].sort((a, b) => {
                        return isBefore(new Date(a.start_date), new Date(b.start_date)) ?
                            -1 :
                            1;
                    })
                }));
            } else {
                state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.push({
                    versionInfo: action.payload.versionInfo,
                    priceRanges: action.payload.priceRanges?.map((range) => ({
                        ...range,
                        periods: [...range.periods].sort((a, b) => {
                            return isBefore(new Date(a.start_date), new Date(b.start_date)) ?
                                -1 :
                                1;
                        })
                    }))
                });
            }
        },
        setPricesEditPeriodValue: <K extends keyof PriceRange['periods'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                periodId: InputId,
                key: K,
                value: PriceRange['periods'][0][K]
            }>
        ): void => {
            const period = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((item) => {
                return item.id === action.payload.priceRangeId;
            })?.periods.find((item) => {
                return item.id === action.payload.periodId;
            });
            if (period) {
                if (action.payload.key === 'start_date' || action.payload.key === 'end_date') {
                    if (action.payload.value instanceof Date && !isValid(action.payload.value)) {
                        // saisie clavier non parsable (mauvais format de date) : on refuse la
                        // valeur pour ne jamais stocker un Invalid Date, qui ferait planter
                        // tous les new Date(...).toISOString() en aval
                        period[action.payload.key] = '';
                        period[`${(action.payload.key as unknown as 'start_date' | 'end_date')}_error`] = i18next.t<string>('shared.date-format-error');
                    } else {
                        period[`${(action.payload.key as unknown as 'start_date' | 'end_date')}_error`] = undefined;
                        period[action.payload.key] = isValid(action.payload.value) ? (action.payload.value as Date)?.toISOString() : action.payload.value;
                    }
                } else {
                    period[action.payload.key] = action.payload.value;
                }
            }
        },
        setPricesEditTerrestrialPriceValue: <K extends keyof PriceRange['terrestrialPrices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                key: K,
                value: PriceRange['terrestrialPrices'][0][K]
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price) {
                price[action.payload.key] = action.payload.value;
            }
        },
        updatePricesEditTerrestrialPriceValue: <K extends keyof PriceRange['terrestrialPrices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                updated_price: Record<K, PriceRange['terrestrialPrices'][0][K]>
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price) {
                Object.entries(action.payload.updated_price).forEach(([key, value]) => {
                    price[key] = value;
                    if (price[`${key}_error` as keyof typeof price]) {
                        price[`${key}_error` as keyof typeof price] = undefined;
                    }
                });
            }
        },
        addPricesEditTerrestrialPriceChildPrices: (
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price) {
                if (!price.child_prices) {
                    price.child_prices = [];
                }
                price.child_prices.push({
                    min_age_child: null,
                    max_age_child: null,
                    prices: [{
                        price: '',
                        selling_price: '',
                        com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                        markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                    }]
                });
            }
        },
        deletePricesEditTerrestrialPriceChildPrices: (
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                child_prices_id: number
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price && price.child_prices) {
                price.child_prices = price.child_prices.filter((_, index) => index !== action.payload.child_prices_id);
            }
        },
        updatePricesEditTerrestrialPriceChildPrices: <K extends keyof PriceRange['terrestrialPrices'][0]['child_prices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                child_prices_id: number,
                key: K,
                value: Record<K, PriceRange['terrestrialPrices'][0]['child_prices'][0][K]>
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price && price.child_prices && price.child_prices[action.payload.child_prices_id]) {
                const childPrice = price.child_prices[action.payload.child_prices_id];
                Object.entries(action.payload.value).forEach(([key, value]) => {
                    (childPrice as any)[key] = value;
                    // Effacer l'erreur associée si elle existe
                    const errorKey = `${key}_error`;
                    if ((childPrice as any)[errorKey]) {
                        (childPrice as any)[errorKey] = undefined;
                    }
                });
            }
        },
        addPricesEditTerrestrialPriceChildPriceInfant: (
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                child_price_index: number
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price && price.child_prices && price.child_prices[action.payload.child_price_index]) {
                const childPrice = price.child_prices[action.payload.child_price_index];
                if (!childPrice.prices) {
                    childPrice.prices = [];
                }
                childPrice.prices.push({
                    price: '',
                    selling_price: '',
                    com_price: state.contract_data?.global_com === null || state.contract_data?.global_com === undefined || state.contract_data?.global_com === '' ? '' : Number(state.contract_data?.global_com),
                    markup_price: state.contract_data?.global_markup === null || state.contract_data?.global_markup === undefined || state.contract_data?.global_markup === '' ? '' : Number(state.contract_data?.global_markup),
                });
            }
        },
        deletePricesEditTerrestrialPriceChildPriceInfant: (
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                child_price_index: number,
                price_index: number
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price && price.child_prices && price.child_prices[action.payload.child_price_index]) {
                const childPrice = price.child_prices[action.payload.child_price_index];
                if (childPrice.prices) {
                    childPrice.prices = childPrice.prices.filter((_, index) => index !== action.payload.price_index);
                }
            }
        },
        updatePricesEditTerrestrialPriceChildPriceInfant: <K extends keyof PriceRange['terrestrialPrices'][0]['child_prices'][0]['prices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                modelId: InputId,
                child_price_index: number,
                price_index: number,
                updated_price: Record<K, PriceRange['terrestrialPrices'][0]['child_prices'][0]['prices'][0][K]>
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.terrestrialPrices.find((terrestrialPrice) => {
                return terrestrialPrice.id === action.payload.modelId;
            });
            if (price && price.child_prices && price.child_prices[action.payload.child_price_index]) {
                const childPrice = price.child_prices[action.payload.child_price_index];
                if (childPrice.prices && childPrice.prices[action.payload.price_index]) {
                    const infantPrice = childPrice.prices[action.payload.price_index];
                    Object.entries(action.payload.updated_price).forEach(([key, value]) => {
                        (infantPrice as any)[key] = value;
                        // Effacer l'erreur associée si elle existe
                        const errorKey = `${key}_error`;
                        if ((infantPrice as any)[errorKey]) {
                            (infantPrice as any)[errorKey] = undefined;
                        }
                    });
                }
            }
        },
        setPricesEditFlightPriceValue: <K extends keyof PriceRange['flightPrices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId,
                key: K,
                value: PriceRange['flightPrices'][0][K]
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.flightPrices.find((flightPrice) => {
                return flightPrice.id === action.payload.priceId;
            });
            if (price) {
                price[action.payload.key] = action.payload.value;
            }
        },
        updatePricesEditFlightPriceValue: <K extends keyof PriceRange['flightPrices'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                priceId: InputId,
                updated_price: Record<K, PriceRange['flightPrices'][0][K]>
            }>
        ): void => {
            const price = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.flightPrices.find((flightPrice) => {
                return flightPrice.id === action.payload.priceId;
            });
            if (price) {
                Object.entries(action.payload.updated_price).forEach(([key, value]) => {
                    price[key] = value;
                    if (price[`${key}_error` as keyof typeof price]) {
                        price[`${key}_error` as keyof typeof price] = undefined;
                    }
                });
            }
        },
        setPricesEditPromotionValue: <K extends keyof PriceRange['promotions'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                promotionId: InputId,
                key: K,
                value: PriceRange['promotions'][0][K]
            }>
        ): void => {
            const promotion = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.promotions.find((promotion) => {
                return promotion.id === action.payload.promotionId;
            });
            if (promotion) {
                promotion[action.payload.key] = action.payload.value;
            }
        },
        updatePricesEditPromotionValue: <K extends keyof PriceRange['promotions'][0]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                priceRangeId: InputId,
                promotionId: InputId,
                updated_price: Record<K, PriceRange['promotions'][0][K]>
            }>
        ): void => {
            const promotion = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.priceRanges.find((priceRange) => {
                return priceRange.id === action.payload.priceRangeId;
            })?.promotions.find((promotion) => {
                return promotion.id === action.payload.promotionId;
            });
            if (promotion) {
                Object.entries(action.payload.updated_price).forEach(([key, value]) => {
                    promotion[key] = value;
                });
            }
        },
        setPricesTableEditValue: <K extends keyof CircuitSliceState['pricesTableEdit'][`${number}-${number}`]>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                key: K,
                value: CircuitSliceState['pricesTableEdit'][`${number}-${number}`][K]
            }>
        ): void => {
            const contractPrices = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices[action.payload.key] = action.payload.value;
            } else {
                state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`] = {
                    state: 'loading',
                    parameters: {
                        datesMode: 'FREE',
                        datesSelection: 'DATE_PICKER',
                    },
                    inputs: [],
                    table: []
                };
            }
        },
        setPricesTableEditParameters: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                parameters: CircuitSliceState['pricesTableEdit'][`${number}-${number}`]['parameters']
            }>
        ): void => {
            const contractPrices = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.parameters = action.payload.parameters;
            }
        },
        setPricesTableEditPeriodValue: <K extends keyof PricesTablePeriod>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                periodId: InputId,
                key: K,
                value: PricesTablePeriod[K]
            }>
        ): void => {
            const period = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            })?.periods.find((item) => {
                return item.id === action.payload.periodId;
            });
            if (period) {
                if (action.payload.key === 'startDate' || action.payload.key === 'endDate') {
                    if (action.payload.value instanceof Date && !isValid(action.payload.value)) {
                        // saisie clavier non parsable (mauvais format de date) : on refuse la
                        // valeur pour ne jamais stocker un Invalid Date
                        period[action.payload.key] = null;
                        period[`${(action.payload.key as unknown as 'startDate' | 'endDate')}Error`] = i18next.t<string>('shared.date-format-error');
                    } else {
                        period[`${(action.payload.key as unknown as 'startDate' | 'endDate')}Error`] = undefined;
                        period[action.payload.key] = isValid(action.payload.value) ? (action.payload.value as Date)?.toISOString() : action.payload.value;
                    }
                } else {
                    period[action.payload.key] = action.payload.value;
                }
            }
        },
        setPricesTableEditPriceValue: <K extends keyof PricesTableItemPrice>(
            state: CircuitSliceState,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                versionInfo: VersionInfo,
                periodId: InputId,
                airportsListId?: InputId,
                key: K,
                value: PricesTableItemPrice[K]
            }>
        ): void => {
            const item = state.pricesTableEdit[`${action.payload.circuitId}-${action.payload.contractId}`]?.inputs.find((item) => {
                return item.versionInfo.version === action.payload.versionInfo.version &&
                    item.versionInfo.variant === action.payload.versionInfo.variant;
            });
            const price = item?.prices.find((price) => {
                return price.periodId === action.payload.periodId &&
                    (!action.payload.airportsListId || price.airportsListId === action.payload.airportsListId);
            });
            if (price) {
                price[action.payload.key] = action.payload.value;
            } else if (item) {
                item.prices.push({
                    id: generateId(),
                    periodId: action.payload.periodId,
                    airportsListId: action.payload.airportsListId,
                    flightPrice: '0',
                    terrestrialPrice: '0',
                    [action.payload.key]: action.payload.value
                });
            }
        },
        setItinerary: (
            state,
            action: PayloadAction<{
                circuit: number,
                data: CircuitSliceState['itinerary'][0]
            }>
        ): void => {
            state.itinerary[action.payload.circuit] = action.payload.data;
        },
        setItineraryByDay: (
            state,
            action: PayloadAction<{
                circuit: number,
                data: CircuitSliceState['itineraryByDay'][0]
            }>
        ): void => {
            state.itineraryByDay[action.payload.circuit] = action.payload.data;
        },
        setProducts: (
            state,
            action: PayloadAction<CircuitSliceState['products']>
        ): void => {
            state.products = action.payload;
        },
        updateItineraryStepPicture: (
            state,
            action: PayloadAction<Picture>
        ): void => {
            state.itineraryByDay = mapValues(
                state.itineraryByDay,
                (value) => {
                    if (value.state === 'success') {
                        return {
                            ...value,
                            data: value.data.map((item) => {
                                if (item.picture?.id === action.payload.id) {
                                    return {
                                        ...item,
                                        picture: action.payload
                                    };
                                }
                                return item;
                            })
                        };
                    }
                    return value;
                }
            );
        },
        updatePricesEditPeriods: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                periods: PricePeriod[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.periods = contractPrices.periods.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.periods = contractPrices.periods.map((item) => {
                    const newPeriod = action.payload.periods.find((newItem) => item.id === newItem.id);
                    if (newPeriod) {
                        return newPeriod;
                    }
                    return item;
                })?.sort((a, b) => {
                    return isBefore(new Date(a.start_date), new Date(b.start_date)) ?
                        -1 :
                        1;
                });
                const newPeriods = action.payload.periods.filter((newItem) => {
                    return !contractPrices.periods.map((item) => item.id).includes(newItem.id);
                })?.sort((a, b) => {
                    return isBefore(new Date(a.start_date), new Date(b.start_date)) ?
                        -1 :
                        1;
                });
                contractPrices.periods = contractPrices.periods.concat(newPeriods);
            }
        },
        updatePricesEditTerrestrialPrices: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                prices: TerrestrialPrice[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.terrestrialPrices = contractPrices.terrestrialPrices.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.terrestrialPrices = contractPrices.terrestrialPrices.map((item) => {
                    const newPrice = action.payload.prices.find((newItem) => item.id === newItem.id);
                    if (newPrice) {
                        return newPrice;
                    }
                    return item;
                });
                const newPrices = action.payload.prices.filter((newItem) => {
                    return !contractPrices.terrestrialPrices.map((item) => item.id).includes(newItem.id);
                });
                contractPrices.terrestrialPrices = contractPrices.terrestrialPrices.concat(newPrices);
            }
        },
        updatePricesEditFlightPrices: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                prices: FlightPrice[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.flightPrices = contractPrices.flightPrices.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.flightPrices = contractPrices.flightPrices.map((item) => {
                    const newPrice = action.payload.prices.find((newItem) => item.id === newItem.id);
                    if (newPrice) {
                        return newPrice;
                    }
                    return item;
                });
                const newPrices = action.payload.prices.filter((newItem) => {
                    return !contractPrices.flightPrices.map((item) => item.id).includes(newItem.id);
                });
                contractPrices.flightPrices = contractPrices.flightPrices.concat(newPrices);
            }
        },
        updatePricesEditPromotions: (
            state,
            action: PayloadAction<{
                circuitId: number,
                contractId: number,
                promotions: Promotion[],
                deleted: number[]
            }>
        ): void => {
            const contractPrices = state.pricesEdit[`${action.payload.circuitId}-${action.payload.contractId}`];
            if (contractPrices) {
                contractPrices.promotions = contractPrices.promotions.filter((item) => {
                    return !action.payload.deleted.includes(item.id);
                });
                contractPrices.promotions = contractPrices.promotions.map((item) => {
                    const newPromotion = action.payload.promotions.find((newItem) => item.id === newItem.id);
                    if (newPromotion) {
                        return newPromotion;
                    }
                    return item;
                });
                const newPromotions = action.payload.promotions.filter((newItem) => {
                    return !contractPrices.promotions.map((item) => item.id).includes(newItem.id);
                });
                contractPrices.promotions = contractPrices.promotions.concat(newPromotions);
            }
        },
        updateProducts: (
            state,
            action: PayloadAction<{ ids: number[], lock: boolean }>
        ): void => {
            state.products = state.products.state === 'success' ?
                {
                    ...state.products,
                    data: {
                        ...state.products.data,
                        results: state.products.data.results.map((item: Product) => {
                            if (action.payload.ids.includes(item.id)) {
                                return {
                                    ...item,
                                    deletion_restriction: action.payload.lock
                                };
                            }
                            return item;
                        })
                    }
                } :
                state.products;
        },
        updateProductsStack: (
            state,
            action: PayloadAction<{ ids: number[], is_stack_price: boolean }>
        ): void => {
            state.products = state.products.state === 'success' ?
                {
                    ...state.products,
                    data: {
                        ...state.products.data,
                        results: state.products.data.results.map((item: Product) => {
                            if (action.payload.ids.includes(item.id)) {
                                return {
                                    ...item,
                                    is_stack_price: action.payload.is_stack_price
                                };
                            }
                            return item;
                        })
                    }
                } :
                state.products;
        },
        updateProductsReferenceProductToReplace: (
            state,
            action: PayloadAction<{ ids: number[], is_reference_product_to_replace: boolean }>
        ): void => {
            state.products = state.products.state === 'success' ?
                {
                    ...state.products,
                    data: {
                        ...state.products.data,
                        results: state.products.data.results.map((item: Product) => {
                            if (action.payload.ids.includes(item.id)) {
                                return {
                                    ...item,
                                    is_reference_product_to_replace: action.payload.is_reference_product_to_replace
                                };
                            }
                            return item;
                        })
                    }
                } :
                state.products;
        },
        updateItineraryByDay: (
            state,
            action: PayloadAction<{
                circuit: number,
                data: ItineraryByDay[]
            }>
        ): void => {
            state.itineraryByDay[action.payload.circuit] = {
                state: 'success',
                data: action.payload.data
            };
        },
        setCircuitLocale: (state: CircuitSliceState, action: PayloadAction<number>): void => {
            state.circuit_locale = action.payload;
        },
        setCurrencyList: (state: CircuitSliceState, action: PayloadAction<Currency[]>): void => {
            state.currency_list = action.payload;
        },
        toggleRefresh: (
            state
        ) => {
            state.refresh = !state.refresh;
        },
        setContractsPrices: (state: CircuitSliceState, action: PayloadAction<ContractPrices>): void => {
            state.contractsPrices = action.payload;
        },
        toggleActionsPanel: (state: CircuitSliceState): void => {
            state.actions_panel_open = !state.actions_panel_open;
        },
        setActionsPanelOpen: (state: CircuitSliceState, action: PayloadAction<boolean>): void => {
            state.actions_panel_open = action.payload;
        },
        updateMarkAsSpecial: (
            state: CircuitSliceState, action: PayloadAction<boolean>
        ): void => {
            state.mark_as_special = action.payload;
        },
        deletePictures: (
            state: CircuitSliceState,
            action: PayloadAction<number>
        ): CircuitSliceState => ({
            ...state,
            pictures: state.pictures.filter(
                (element, index) => index !== action.payload
            )
        }),
        updateVat: (state: CircuitSliceState, action: PayloadAction<number>): void => {
            state.vat = action.payload;
        },
        setDepartureAirports: (state: CircuitSliceState, action: PayloadAction<DepartureAirport[]>): void => {
            state.departure_airports = action.payload;
        },
        updateMarginMasterMarkup: (state: CircuitSliceState, action: PayloadAction<boolean>): void => {
            state.margin_master_markup = action.payload;
        },
        setProductToReplace: (state: CircuitSliceState, action: PayloadAction<Product | null>): void => {
            state.product_to_replace = action.payload;
        },
        updateProdcutReplacementDate: (state: CircuitSliceState, action: PayloadAction<CircuitSliceState['product_replacement_date']>): void => {
            state.product_replacement_date = action.payload;
        },
        setTripsSelectedToReplace: (state: CircuitSliceState, action: PayloadAction<CircuitSliceState['trips_to_replace_selected']>): void => {
            state.trips_to_replace_selected = action.payload;
        },
        setProductHistory: (state: CircuitSliceState, action: PayloadAction<CircuitSliceState['product_history']>): void => {
            state.product_history = action.payload;
        },
        setIsEditingStock: (state: CircuitSliceState, action: PayloadAction<boolean>): void => {
            state.is_editing_stock = action.payload;
        },
        updateCircuitInfoErrors: updateCircuitInfoErrorsReducer,
        setColumns: setColumnsReducer,
        toggleColumn: toggleColumnReducer,
        addFlightParamsListConfigurationPreference: addFlightParamsListConfigurationPreferenceReducer,
        setFlightParamsListConfigurationPreference: setFlightParamsListConfigurationPreferenceReducer,
        updateFlightParamsList: updateFlightParamsListReducer,
        updateFlightParamsListCriteria: updateFlightParamsListCriteriaReducer,
        updateFlightParams: updateFlightParamsReducer,
        updateFlightParamsListLoading: updateFlightParamsListLoadingReducer,
        updateFlightParamsListCount: updateFlightParamsListCountReducer,
        addDepartureAirportsListConfigurationPreference: addDepartureAirportsListConfigurationPreferenceReducer,
        setDepartureAirportsListConfigurationPreference: setDepartureAirportsListConfigurationPreferenceReducer,
        updateDepartureAirportsList: updateDepartureAirportsListReducer,
        updateDepartureAirportsListCriteria: updateDepartureAirportsListCriteriaReducer,
        updateDepartureAirports: updateDepartureAirportsReducer,
        updateDepartureAirportsListLoading: updateDepartureAirportsListLoadingReducer,
        updateDepartureAirportsListCount: updateDepartureAirportsListCountReducer,
        setDepartureAirportsColumns: setDepartureAirportsColumnsReducer,
        toggleDepartureAirportsColumn: toggleDepartureAirportsColumnReducer,
        addRoomListConfigurationPreference: addRoomListConfigurationPreferenceReducer,
        setRoomListConfigurationPreference: setRoomListConfigurationPreferenceReducer,
        updateRoomList: updateRoomListReducer,
        updateRoomListCriteria: updateRoomListCriteriaReducer,
        updateRoom: updateRoomReducer,
        updateRoomListLoading: updateRoomListLoadingReducer,
        updateRoomListCount: updateRoomListCountReducer,
        setRoomColumns: setRoomColumnsReducer,
        toggleRoomColumn: toggleRoomColumnReducer,
        addMealPlanListConfigurationPreference: addMealPlanListConfigurationPreferenceReducer,
        setMealPlanListConfigurationPreference: setMealPlanListConfigurationPreferenceReducer,
        updateMealPlanList: updateMealPlanListReducer,
        updateMealPlanListCriteria: updateMealPlanListCriteriaReducer,
        updateMealPlan: updateMealPlanReducer,
        updateMealPlanListLoading: updateMealPlanListLoadingReducer,
        updateMealPlanListCount: updateMealPlanListCountReducer,
        setMealPlanColumns: setMealPlanColumnsReducer,
        toggleMealPlanColumn: toggleMealPlanColumnReducer,
        setContractData: setContractDataReducer,
        updateCircuitDates: updateCircuitDatesReducer,
        setUpdateStock: setUpdateStockReducer,
        setUpdateStockConfirmation: setUpdateStockConfirmationReducer,
        updateAllowedCms: updateAllowedCmsReducer,

        // special offer
        addSpecialOfferListConfigurationPreference: addSpecialOfferListConfigurationPreferenceReducer,
        setSpecialOfferListConfigurationPreference: setSpecialOfferListConfigurationPreferenceReducer,
        updateSpecialOfferListCount: updateSpecialOfferListCountReducer,
        setSpecialOfferColumns: setSpecialOfferColumnsReducer,
        toggleSpecialOfferColumn: toggleSpecialOfferColumnReducer,
        updateSpecialOfferList: updateSpecialOfferListReducer,
        updateSpecialOfferListCriteria: updateSpecialOfferListCriteriaReducer,
        updateSpecialOffer: updateSpecialOfferReducer,
        updateSpecialOfferListLoading: updateSpecialOfferListLoadingReducer,
        toggleSpecialOfferRefresh: toggleSpecialOfferRefreshReducer,

        // circuit allowed provider
        updateCircuitAllowedProviders: (state: CircuitSliceState, action: PayloadAction<any[]>): CircuitSliceState => ({
            ...state, circuit_allowed_providers: action.payload
        }),

        // rooming list
        updateRoomingListExportCriteria: updateRoomingListExportCriteriaReducer,
        setRoomingListLoading: setRoomingListLoadingReducer,
        updateRoomingListGeneration: updateRoomingListGenerationReducer,
        updateRoomingListSelectedTemplate: updateRoomingListSelectedTemplateReducer,
        setCircuitIds: setCircuitIdsReducer,
        setRoomingListGenerationLoaded: setRoomingListGenerationLoadedReducer,

        setRoomingListHistory: setRoomingListHistoryReducer,
        setRoomingListHistoryCriteria: setRoomingListHistoryCriteriaReducer,
        setRoomingListHistorySearch: setRoomingListHistorySearchReducer,
        setRoomingListHistoryLoaded: setRoomingListHistoryLoadedReducer,

        // price update
        setIsPriceByRoom: (state: CircuitSliceState, action: PayloadAction<boolean>): void => {
            state.is_price_by_room = action.payload;
        },

        // blackouts
        setAllBlackouts: (state: CircuitSliceState, action: PayloadAction<any[]>): void => {
            state.all_blackouts = action.payload;
            state.all_blackouts_status = 'loaded';
        },
        refreshAllBlackouts: (state: CircuitSliceState): void => {
            // state.all_blackouts = [];
            state.all_blackouts_status = 'not-loaded';
        },
        // column resize
        resizeFlightParamsColumn: (state: CircuitSliceState, action: PayloadAction<ColumnResizePayload>): void => {
            state.flight_params_list_configuration.columns = applyColumnResize(state.flight_params_list_configuration.columns, action.payload);
        },
        resizeDepartureAirportsColumn: (state: CircuitSliceState, action: PayloadAction<ColumnResizePayload>): void => {
            state.departure_airports_list_configuration.columns = applyColumnResize(state.departure_airports_list_configuration.columns, action.payload);
        },
        resizeRoomColumn: (state: CircuitSliceState, action: PayloadAction<ColumnResizePayload>): void => {
            state.room_list_configuration.columns = applyColumnResize(state.room_list_configuration.columns, action.payload);
        },
        resizeMealPlanColumn: (state: CircuitSliceState, action: PayloadAction<ColumnResizePayload>): void => {
            state.meal_plan_list_configuration.columns = applyColumnResize(state.meal_plan_list_configuration.columns, action.payload);
        },
        resizeSpecialOfferColumn: (state: CircuitSliceState, action: PayloadAction<ColumnResizePayload>): void => {
            state.special_offer_list_configuration.columns = applyColumnResize(state.special_offer_list_configuration.columns, action.payload);
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer)
            .addCase(addPictures, addPicturesReducer)
            .addCase(setPicture, setPictureReducer);
    }
});

export const {
    toggleActionsPanel,
    setActionsPanelOpen,
    toggleRefreshFlightParams,
    setFlightParams,
    setEditFlightParamsSetps,
    toggleRefreshDepartureAirports,
    setDepartureAirportsParams,
    setEditDepartureAirportsSetps,
    toggleRefreshRoom,
    setRoom,
    setEditRoomSetps,
    toggleRefreshMealPlan,
    setMealPlan,
    setEditMealPlanSetps,
    setOpenCopyModal,
    toggleOpenCopyModal,
    addPricesEditPriceRange,
    addPricesEditPeriod,
    addPricesEditTerrestrialPrice,
    addPricesEditFlightPrice,
    addPricesEditOfflineFlightPrices,
    addPricesEditOfflineFlightPriceRanges,
    addPricesEditPromotion,
    addPricesTableEditAirport,
    addPricesTableEditAirportsList,
    addPricesTableEditPeriod,
    deletePricesEditAirport,
    deletePricesEditPriceRange,
    deletePricesEditPeriod,
    deletePricesEditTerrestrialPrice,
    deletePricesEditPromotion,
    deletePricesEditFlightPrice,
    deletePricesTableEditAirport,
    deletePricesTableEditAirportsList,
    deletePricesTableEditPeriod,
    setCancelationRules,
    setGlobalCancelationRules,
    setPricesEditValue,
    setPricesEditParameters,
    setPricesEditPriceRanges,
    setPricesEditPeriodValue,
    setPricesEditTerrestrialPriceValue,
    setPricesEditFlightPriceValue,
    setPricesEditPromotionValue,
    updatePricesEditTerrestrialPriceValue,
    addPricesEditTerrestrialPriceChildPrices,
    deletePricesEditTerrestrialPriceChildPrices,
    updatePricesEditTerrestrialPriceChildPrices,
    addPricesEditTerrestrialPriceChildPriceInfant,
    deletePricesEditTerrestrialPriceChildPriceInfant,
    updatePricesEditTerrestrialPriceChildPriceInfant,
    updatePricesEditFlightPriceValue,
    updatePricesEditPromotionValue,
    setPricesTableEditValue,
    setPricesTableEditParameters,
    setPricesTableEditPeriodValue,
    setPricesTableEditPriceValue,
    setItinerary,
    setItineraryByDay,
    setProducts,
    updateItineraryStepPicture,
    updatePricesEditPeriods,
    updatePricesEditTerrestrialPrices,
    updatePricesEditFlightPrices,
    updatePricesEditPromotions,
    updateProducts,
    updateProductsStack,
    updateProductsReferenceProductToReplace,
    updateItineraryByDay,
    setCircuitLocale,
    setCurrencyList,
    toggleRefresh,
    updateCircuitInfoErrors,
    setColumns,
    toggleColumn,
    setDepartureAirportsColumns,
    toggleDepartureAirportsColumn,
    addFlightParamsListConfigurationPreference,
    setFlightParamsListConfigurationPreference,
    updateFlightParamsList,
    updateFlightParamsListCriteria,
    updateFlightParams,
    updateFlightParamsListLoading,
    updateFlightParamsListCount,
    setContractsPrices,
    addDepartureAirportsListConfigurationPreference,
    setDepartureAirportsListConfigurationPreference,
    updateDepartureAirportsList,
    updateDepartureAirportsListCriteria,
    updateDepartureAirports,
    updateDepartureAirportsListLoading,
    updateDepartureAirportsListCount,
    setRoomColumns,
    toggleRoomColumn,
    addRoomListConfigurationPreference,
    setRoomListConfigurationPreference,
    updateRoomList,
    updateRoomListCriteria,
    updateRoom,
    updateRoomListLoading,
    updateRoomListCount,
    setMealPlanColumns,
    toggleMealPlanColumn,
    addMealPlanListConfigurationPreference,
    setMealPlanListConfigurationPreference,
    updateMealPlanList,
    updateMealPlanListCriteria,
    updateMealPlan,
    updateMealPlanListLoading,
    updateMealPlanListCount,
    setSuspendedMargin,
    addMarginsConfigurationPreference,
    setMarginsConfigurationPreference,
    toggleMarginColumn,
    setCircuitMargins,
    updateCircuitMarginsCriteria,
    updateCircuitMargin,
    updateCircuitMarginsLoading,
    updateCircuitMarginsCount,
    setMarginColumns,
    setContractData,
    updateCircuitDates,
    setUpdateStock,
    updateMarkAsSpecial,
    setUpdateStockConfirmation,
    deletePictures,
    updateVat,
    updateMarginMasterMarkup,
    setProductToReplace,
    setDepartureAirports,
    updateAllowedCms,
    addSpecialOfferListConfigurationPreference,
    setSpecialOfferListConfigurationPreference,
    updateSpecialOfferListCount,
    setSpecialOfferColumns,
    toggleSpecialOfferColumn,
    updateSpecialOfferList,
    updateSpecialOfferListCriteria,
    updateSpecialOffer,
    updateSpecialOfferListLoading,
    toggleSpecialOfferRefresh,
    updateCircuitAllowedProviders,

    updateRoomingListExportCriteria,
    setRoomingListLoading,
    updateRoomingListGeneration,
    updateRoomingListSelectedTemplate,
    setCircuitIds,
    setRoomingListGenerationLoaded,

    setRoomingListHistory,
    setRoomingListHistoryCriteria,
    setRoomingListHistorySearch,
    setRoomingListHistoryLoaded,
    updateProdcutReplacementDate,

    setTripsSelectedToReplace,
    setProductHistory,
    setIsPriceByRoom,
    setIsEditingStock,

    setAllBlackouts,
    refreshAllBlackouts,

    resizeFlightParamsColumn,
    resizeDepartureAirportsColumn,
    resizeRoomColumn,
    resizeMealPlanColumn,
    resizeSpecialOfferColumn
} = circuitSlice.actions;